<template>
  <div class="team-page">
    <div class="team-header">
      <div class="team-header-content">
        <div class="team-title-row">
          <UserInfoPopup
            :user-id="userId"
            :user-name="userName"
            :current-role="currentRole"
            :role-id="roleId"
            :territory-type="effectiveTerritoryType"
          >
            <template #trigger="{ open }">
              <div class="team-avatar" @click="open">
                <van-icon name="manager" size="24" />
              </div>
            </template>
          </UserInfoPopup>
          <div>
            <div class="page-title-line">
              <span class="page-title">{{ pageTitle }}</span>
              <span v-if="scopeLabel" class="page-scope-tag">{{ scopeLabel }}</span>
            </div>
            <div class="page-subtitle">{{ $t('manager_overview_report_subtitle', {role: roleLabel}) }}</div>
          </div>
        </div>
        <div v-if="roleActions.length > 1" class="team-role-switcher" @click="showRoleSelector = true">
          <span class="role-text">{{ currentRoleDisplay }}</span>
          <van-icon name="exchange" size="14" />
        </div>
        <div v-if="isManagerRole" class="header-period-filter">
          <van-dropdown-menu active-color="#aa341b" :z-index="3000">
            <van-dropdown-item
              v-model="periodType"
              :options="periodTypeOptions"
              @change="handlePeriodTypeChange"
            />
          </van-dropdown-menu>
          <van-dropdown-menu active-color="#aa341b" :z-index="3000">
            <van-dropdown-item
              v-model="periodKey"
              :options="periodOptions"
              @change="handlePeriodKeyChange"
            />
          </van-dropdown-menu>
        </div>
      </div>
      <div v-if="isManagerRole" class="header-stats">
        <div>
          <strong>{{ loading ? '--' : memberRows.length }}</strong>
          <span>{{ $t('manager_overview_members') }}</span>
        </div>
        <div>
          <strong>{{ loading ? '--' : `${coveredCustomerCount}/${targetCustomerCount || '--'}` }}</strong>
          <span class="coverage-label header-coverage-label">
            <span>{{ $t('manager_overview_target_customer_coverage') }}</span>
            <span class="coverage-dot">·</span>
            <b>{{ coverageRateText }}</b>
          </span>
        </div>
        <div class="header-stat-right">
          <strong>{{ loading ? '--' : `${abcCoveredCustomerCount}/${abcTargetCustomerCount || '--'}` }}</strong>
          <span class="coverage-label header-coverage-label">
            <span>{{ $t('manager_overview_abc_coverage_short') }}</span>
            <span class="coverage-dot">·</span>
            <b>{{ abcCoverageRateText }}</b>
          </span>
        </div>
      </div>
    </div>

    <div v-if="!isManagerRole" class="state-block">
      <van-empty :description="$t('manager_overview_no_permission')" />
    </div>

    <div v-else-if="loading" class="state-block">
      <div class="loading-progress-card">
        <div class="loading-progress-head">
          <div class="loading-progress-step">
            <span>{{ $t('manager_overview_loading_step', {step: loadingProgress.currentLabel || $t('loading')}) }}</span>
            <small>{{ $t('manager_overview_loading_position', {current: loadingCurrentModuleIndex, total: loadingProgress.totalModules}) }}</small>
          </div>
          <div class="loading-progress-percent">{{ loadingProgress.percent }}%</div>
        </div>
        <div class="loading-progress-track">
          <div class="loading-progress-fill" :style="{width: `${loadingProgress.percent}%`}"></div>
        </div>
      </div>
    </div>

    <div v-else class="team-content">
      <div v-if="loadErrors.length" class="warning-box">
        <div v-for="item in loadErrors" :key="item">{{ item }}</div>
      </div>

      <section class="abc-coverage-section section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t('manager_overview_abc_coverage_breakdown') }}</div>
            <div class="section-desc">{{ $t('manager_overview_abc_coverage_breakdown_desc') }}</div>
          </div>
          <div class="abc-coverage-total">
            <strong>{{ abcCoveredCustomerCount }}/{{ abcTargetCustomerCount || '--' }}</strong>
            <span>{{ $t('manager_overview_abc_coverage_total') }} · {{ abcCoverageRateText }}</span>
          </div>
        </div>
        <div class="abc-coverage-grid">
          <div
            v-for="item in abcCoverageRows"
            :key="item.level"
            :class="['abc-coverage-card', `level-${item.level.toLowerCase()}`]"
          >
            <span class="abc-level">{{ item.level }}</span>
            <strong>{{ item.coveredCustomerCount }}/{{ item.targetCustomerCount || '--' }}</strong>
            <small>{{ $t('manager_overview_coverage_rate') }} {{ item.coverageRateText }}</small>
          </div>
        </div>
      </section>

      <template v-if="isOverviewReport">
        <section class="summary-section section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('manager_overview_execution_overview') }}</div>
            </div>
            <div class="section-actions">
              <span class="section-period">{{ periodRangeText }}</span>
              <button type="button" class="ai-action-btn" @click="openTeamAiAnalysis('overall')">
                <van-icon name="comment-o" />
                <span>{{ $t('manager_overview_ai_analysis') }}</span>
              </button>
            </div>
          </div>
          <div class="metric-grid">
            <div v-for="item in summaryMetrics" :key="item.label" class="metric-card">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
              <div v-if="item.desc" class="metric-desc">{{ item.desc }}</div>
            </div>
          </div>
        </section>

        <section class="summary-section section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('manager_overview_execution_quality') }}</div>
            </div>
            <div class="section-actions">
              <span class="section-period">{{ periodRangeText }}</span>
              <button type="button" class="ai-action-btn" @click="openTeamAiAnalysis('quality')">
                <van-icon name="comment-o" />
                <span>{{ $t('manager_overview_ai_analysis') }}</span>
              </button>
            </div>
          </div>
          <div class="quality-list">
            <div v-for="item in qualityMetrics" :key="item.label" class="quality-row">
              <div class="quality-head">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
              <div class="progress-track">
                <div class="progress-fill" :style="{width: item.progress + '%'}"></div>
              </div>
              <div class="quality-desc">{{ item.desc }}</div>
            </div>
          </div>
        </section>
      </template>

      <section v-if="isOverviewReport" class="summary-section section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t('manager_overview_analysis_title') }}</div>
            <div class="section-desc">{{ $t('manager_overview_analysis_desc') }}</div>
          </div>
          <div class="section-actions section-actions-vertical">
            <span class="section-period">{{ $t('manager_overview_current_latest') }}</span>
            <button type="button" class="ai-action-btn" @click="openTeamAiAnalysis('product-segment')">
              <van-icon name="comment-o" />
              <span>{{ $t('manager_overview_ai_analysis') }}</span>
            </button>
          </div>
        </div>
        <div v-if="analysisLoading" class="section-loading">
          <van-loading color="#aa341b" size="20px">{{ $t('manager_overview_loading_analysis') }}</van-loading>
        </div>
        <template v-else>
          <div class="analysis-summary">
            <div class="section-title">{{ $t('manager_overview_segment_overview') }}</div>
            <div class="section-desc">{{ $t('manager_overview_segment_desc') }}</div>
          </div>
          <div v-if="productSegmentRows.length" class="product-overview-list">
            <div v-for="product in productSegmentRows" :key="product.id" class="product-overview-card">
              <div class="product-overview-head">
                <div>
                  <div class="product-name">{{ product.name }}</div>
                  <div class="product-meta">{{ $t('manager_overview_abc_target_customers_count', {count: product.targetCustomerCount}) }}</div>
                </div>
              </div>
              <div class="segment-bar" :aria-label="$t('manager_overview_segment_distribution_aria', {product: product.name})">
                <div
                  v-for="segment in product.segments"
                  :key="segment.id"
                  class="segment-bar-item"
                  :style="{ width: segment.widthStyle, backgroundColor: segment.color }"
                  :title="segment.labelText"
                >
                  <span>{{ segment.shortLabelText }}</span>
                </div>
              </div>
              <div v-if="product.segments.length" class="segment-legend-list">
                <span
                  v-for="segment in product.segments"
                  :key="segment.id"
                  class="segment-legend-item"
                >
                  <i :style="{ backgroundColor: segment.color }"></i>
                  <span>{{ segment.labelText }}</span>
                </span>
              </div>
            </div>
          </div>
          <van-empty v-else :description="$t('manager_overview_no_product_segment_data')" />
        </template>
      </section>

      <section v-if="isTeamReport" class="member-section section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t('manager_overview_member_execution') }}</div>
            <div class="section-desc">
              {{ $t('manager_overview_member_desc') }}
              <span v-if="showMemberTerritoryFilters">{{ $t('manager_overview_member_current_display', {filtered: filteredMemberRows.length, total: memberRows.length}) }}</span>
            </div>
          </div>
          <div class="section-actions">
            <span class="section-period">{{ periodRangeText }}</span>
          </div>
        </div>
        <div v-if="teamDetailLoading" class="section-loading">
          <van-loading color="#aa341b" size="20px">{{ $t('manager_overview_loading_team_detail') }}</van-loading>
        </div>
        <template v-else>
          <div
            v-if="showMemberTerritoryFilters"
            :class="['member-filter-row', {'single-filter': !showAreaMemberFilter}]"
          >
            <van-dropdown-menu v-if="showAreaMemberFilter" active-color="#aa341b" :z-index="3000">
              <van-dropdown-item
                v-model="selectedAreaTerritoryId"
                :options="areaOptions"
              />
            </van-dropdown-menu>
            <van-dropdown-menu v-if="showDistrictMemberFilter" active-color="#aa341b" :z-index="3000">
              <van-dropdown-item
                ref="districtDropdown"
                :title="selectedDistrictOptionTitle"
              >
                <div class="district-option-list">
                  <button
                    v-for="option in districtOptions"
                    :key="option.value || 'all-districts'"
                    type="button"
                    :class="['district-option-row', {active: option.value === selectedDistrictTerritoryId}]"
                    @click="selectDistrictOption(option)"
                  >
                    <span class="district-option-name">{{ option.text }}</span>
                    <span class="district-option-count">{{ option.count }}</span>
                    <van-icon
                      v-if="option.value === selectedDistrictTerritoryId"
                      class="district-option-check"
                      name="success"
                    />
                  </button>
                </div>
              </van-dropdown-item>
            </van-dropdown-menu>
          </div>
          <div v-if="filteredMemberRows.length" class="member-list">
            <div
              v-for="member in filteredMemberRows"
              :key="member.id"
              class="member-card"
            >
              <div class="member-top" @click="openMemberDetail(member.id)">
                <div>
                  <div class="member-name">{{ member.name }}</div>
                  <div class="member-meta">{{ member.territoryName || member.territoryId || $t('manager_overview_no_territory') }}</div>
                </div>
                <div class="member-side">
                  <div class="member-score">
                    <span>{{ member.coveredCustomerCount }}/{{ member.targetCustomerCountText }}</span>
                    <small class="coverage-label member-coverage-label">
                      <span>{{ $t('manager_overview_target_customer_coverage') }}</span>
                      <span class="coverage-dot">·</span>
                      <b>{{ member.coverageRateText }}</b>
                    </small>
                  </div>
                  <div class="member-score">
                    <span>{{ member.abcCoveredCustomerCount }}/{{ member.abcTargetCustomerCountText }}</span>
                    <small class="coverage-label member-coverage-label">
                      <span>{{ $t('manager_overview_abc_coverage_short') }}</span>
                      <span class="coverage-dot">·</span>
                      <b>{{ member.abcCoverageRateText }}</b>
                    </small>
                  </div>
                  <button type="button" class="member-detail-btn" @click.stop="openMemberDetail(member.id)">
                    <van-icon name="arrow" />
                  </button>
                </div>
              </div>
              <div class="member-abc-breakdown">
                <div v-for="item in member.abcCoverageRows" :key="item.level">
                  <span>{{ item.level }}</span>
                  <strong>{{ item.coveredCustomerCount }}/{{ item.targetCustomerCount || '--' }}</strong>
                  <small>{{ item.coverageRateText }}</small>
                </div>
              </div>
              <div class="member-kpis">
                <div>
                  <strong>{{ member.callCount }}</strong>
                  <span>{{ $t('manager_overview_calls') }}</span>
                </div>
                <div>
                  <strong>{{ member.feedbackCount }}</strong>
                  <span>{{ $t('manager_overview_call_feedback') }}</span>
                </div>
                <div>
                  <strong>{{ member.contentOpenCount }}/{{ member.contentSendCount || '--' }}</strong>
                  <span>{{ $t('manager_overview_content_open_send') }}</span>
                  <small class="member-read-duration">
                    {{ $t('manager_overview_content_avg_read_prefix') }}<span>{{ member.contentAverageOpenDurationText }}</span>{{ $t('manager_overview_content_avg_read_suffix') }}
                  </small>
                </div>
                <div v-if="!hideTaskAndNba">
                  <strong>{{ member.taskExecuted }}/{{ member.taskTotalText }}</strong>
                  <span>{{ $t('manager_overview_task_execution') }}</span>
                </div>
                <div v-if="!hideTaskAndNba">
                  <strong>{{ member.nbaExecuted }}</strong>
                  <span>{{ $t('manager_overview_nba_execution') }}</span>
                </div>
              </div>
              <div v-if="member.productSummaryText" class="member-products">
                {{ $t('manager_overview_product_coverage_prefix') }}{{ member.productSummaryText }}
              </div>
            </div>
          </div>
          <van-empty v-else :description="$t('manager_overview_no_filtered_member_data')" />
        </template>
      </section>

    </div>
    <transition name="detail-slide">
      <section v-if="memberDetailVisible" class="detail-slide-page">
        <div class="detail-nav">
          <button type="button" class="back-btn" @click="backToMemberList">
            <van-icon name="arrow-left" />
          </button>
          <div>
            <div class="section-title">{{ $t('manager_overview_personal_detail') }}</div>
            <div v-if="selectedMember" class="member-meta">{{ selectedMember.name }}</div>
          </div>
        </div>
        <div v-if="selectedMember" class="detail-panel">
          <div class="detail-metrics-header">
            <small>{{ detailMetricPeriodLabel }}</small>
          </div>
          <div class="detail-grid">
            <div
              v-for="item in selectedMemberMetrics"
              :key="item.label"
              class="detail-item"
              :class="{'detail-item-clickable': item.action}"
              :role="item.action ? 'button' : null"
              :tabindex="item.action ? 0 : null"
              @click="handleDetailMetricClick(item)"
              @keyup.enter="handleDetailMetricClick(item)"
            >
              <span class="detail-label">{{ item.label }}</span>
              <strong class="detail-value">
                <span class="detail-value-main">{{ item.value }}</span>
                <span v-if="item.unit" class="detail-value-unit">{{ item.unit }}</span>
              </strong>
              <van-icon v-if="item.action" name="arrow" class="detail-item-arrow" />
            </div>
            <div class="detail-item detail-promotion-card">
              <small class="detail-period">{{ promotionQuarterKey }}</small>
              <span class="detail-label">{{ $t('manager_overview_online_promotion_progress') }}</span>
              <div v-if="selectedMemberPromotionLoading" class="promotion-loading">
                <van-loading color="#aa341b" size="16px">{{ $t('loading') }}</van-loading>
              </div>
              <template v-else>
                <div class="promotion-main">
                  <div>
                    <strong>{{ selectedMemberPromotionStats ? selectedMemberPromotionStats.sendCount : '--' }}</strong>
                    <span>/{{ selectedMemberPromotionStats ? selectedMemberPromotionStats.targetSendCount : '--' }} {{ $t('manager_overview_unit_times_suffix') }}</span>
                  </div>
                  <b>{{ selectedMemberPromotionStats ? formatMetricPercent(selectedMemberPromotionStats.progress) : '--' }}</b>
                </div>
                <div class="promotion-track">
                  <div class="promotion-fill" :style="{width: `${Math.min(selectedMemberPromotionStats ? selectedMemberPromotionStats.progress : 0, 100)}%`}"></div>
                </div>
                <div class="promotion-sub">
                  {{ $t('manager_overview_promotion_open_count') }}
                  <b>{{ selectedMemberPromotionStats ? selectedMemberPromotionStats.openCount : '--' }}</b>
                  {{ $t('manager_overview_unit_times_suffix') }}
                </div>
                <div class="promotion-note">
                  {{ selectedMemberPromotionError ? $t('manager_overview_online_promotion_unavailable') : selectedMemberPromotionNote }}
                </div>
              </template>
            </div>
          </div>
          <div class="product-title">{{ $t('manager_overview_abc_coverage_breakdown') }}</div>
          <div class="detail-abc-coverage-grid">
            <div v-for="item in selectedMember.abcCoverageRows" :key="item.level" class="detail-abc-coverage-item">
              <span>{{ item.level }}</span>
              <strong>{{ item.coveredCustomerCount }}/{{ item.targetCustomerCount || '--' }}</strong>
              <small>{{ $t('manager_overview_coverage_rate') }} {{ item.coverageRateText }}</small>
            </div>
          </div>
          <div class="product-title">{{ $t('manager_overview_product_target_coverage') }}</div>
          <div v-if="selectedMember.productRows.length" class="product-list">
            <div v-for="product in selectedMember.productRows" :key="product.id" class="product-row">
              <div>
                <div class="product-name">{{ product.name }}</div>
                <div class="product-meta">{{ $t('manager_overview_customer_count_pair', {covered: product.coveredCustomerCount, total: product.targetCustomerCount}) }}</div>
              </div>
              <strong>{{ product.coverageRateText }}</strong>
            </div>
          </div>
          <van-empty v-else :description="$t('manager_overview_no_product_customer_data')" />
          <div class="recent-title">{{ $t('manager_overview_recent_calls') }}</div>
          <div v-if="detailRecentCallsLoading" class="detail-loading">
            <van-loading color="#aa341b" size="20px">{{ $t('loading') }}</van-loading>
          </div>
          <div v-else-if="detailRecentCalls.length" class="recent-list">
            <div v-for="call in detailRecentCalls" :key="call.id" class="recent-item">
              <div class="recent-main">
                <span>{{ call.physicianName }}</span>
                <small>{{ call.date }}</small>
              </div>
              <div class="recent-sub">{{ call.institutionName || $t('manager_overview_no_hospital') }}</div>
            </div>
          </div>
          <div v-else-if="detailRecentCallError" class="detail-error">{{ detailRecentCallError }}</div>
          <van-empty v-else :description="$t('manager_overview_no_recent_calls')" />
        </div>
        <van-empty v-else :description="$t('manager_overview_select_member')" />
      </section>
    </transition>

    <transition name="detail-slide">
      <section v-if="memberContentDetailVisible" class="detail-slide-page content-detail-slide-page">
        <div class="detail-nav">
          <button type="button" class="back-btn" @click="closeMemberContentDetail">
            <van-icon name="arrow-left" />
          </button>
          <div>
            <div class="section-title">{{ $t('manager_content_customer_detail') }}</div>
            <div v-if="selectedMember" class="member-meta">
              {{ selectedMember.name }} · {{ detailMetricPeriodLabel }}
            </div>
          </div>
        </div>

        <div class="content-detail-summary">
          <div>
            <strong>{{ memberContentDetailRows.length }}</strong>
            <span>{{ $t('manager_content_reached_customers') }}</span>
          </div>
          <div>
            <strong>{{ memberContentOpenedCount }}</strong>
            <span>{{ $t('manager_content_opened') }}</span>
          </div>
          <div>
            <strong>{{ memberContentUnopenedCount }}</strong>
            <span>{{ $t('manager_content_unopened') }}</span>
          </div>
        </div>

        <div class="content-detail-filters">
          <button
            v-for="option in memberContentFilterOptions"
            :key="option.value"
            type="button"
            :class="{active: memberContentDetailFilter === option.value}"
            @click="memberContentDetailFilter = option.value"
          >
            <span>{{ option.label }}</span>
            <b>{{ option.count }}</b>
          </button>
        </div>

        <div v-if="memberContentDetailLoading" class="detail-loading content-detail-loading">
          <van-loading color="#aa341b" size="20px">{{ $t('loading') }}</van-loading>
        </div>
        <div v-else-if="memberContentDetailError" class="detail-error content-detail-error">
          {{ memberContentDetailError }}
        </div>
        <div v-else-if="filteredMemberContentDetailRows.length" class="content-customer-list">
          <div
            v-for="customer in filteredMemberContentDetailRows"
            :key="customer.physicianId"
            class="content-customer-card"
          >
            <div class="content-customer-main">
              <div class="content-customer-avatar">{{ customer.physicianName.slice(0, 1) }}</div>
              <div class="content-customer-info">
                <strong>{{ customer.physicianName }}</strong>
                <span>{{ customer.institutionName || $t('manager_overview_no_hospital') }}</span>
              </div>
              <span class="content-open-status" :class="{opened: customer.opened}">
                {{ customer.opened ? $t('manager_content_opened') : $t('manager_content_unopened') }}
              </span>
            </div>
            <div class="content-customer-stats">
              <span>{{ $t('manager_content_send_count', {count: customer.sendCount}) }}</span>
              <span>{{ $t('manager_content_open_count', {count: customer.openCount}) }}</span>
            </div>
          </div>
        </div>
        <van-empty v-else :description="$t('manager_content_no_customers')" />
      </section>
    </transition>

    <van-action-sheet
      v-model="showRoleSelector"
      :actions="roleActions"
      :cancel-text="$t('btn_cancel')"
      close-on-click-action
      :description="$t('role_switch_description')"
      @select="onSelectRole"
    />
  </div>
</template>

<script>
import {ActionSheet, DropdownItem, DropdownMenu, Empty, Icon, Loading, Toast} from 'vant';
import {switchRole} from '@/api/user';
import {getLocalStorage, setLocalStorage, isEnableAI, isGBA, isSalesManagerTerritory, normalizeSalesOverviewContext, resolveEnableAI, clearManagerOverviewPeriodState} from '@/utils/index';
import {setToken} from '@/utils/auth';
import UserInfoPopup from './components/UserInfoPopup.vue';
import {
  fetchManagerCampaignEventTaskStats,
  fetchManagerContentOpenStats,
  fetchManagerContentSendStats,
  fetchMemberContentInteractionStats,
  fetchManagerCoveredCustomerStats,
  fetchManagerFeedbackCallStats,
  fetchManagerNbaSuggestionFeedbackStats,
  fetchManagerNbaSuggestionStats,
  fetchManagerPhysicianTypingDefinitions,
  fetchManagerPhysiciansByIds,
  fetchManagerProductSegmentStats,
  fetchManagerRecentCalls,
  fetchManagerSubordinateTerritoryHierarchy,
  fetchManagerTargetCustomerStats,
  fetchManagerTargetProductStats,
} from '@/api/team';

const MANAGER_ROLES = ['DM', 'AM', 'SD'];
const SALES_MANAGER_COPILOT_AGENT = 'SRCopilotSalesManager';
const UNKNOWN_TYPING_ID = 'unknown';
const UNKNOWN_TYPING_NAME = '其他';
const UNKNOWN_TYPING_COLOR = '#94a3b8';
const SEGMENT_BAR_COLORS = [
  '#0f59ff',
  '#976fe2',
  '#fcdb73',
  '#17a772',
  '#ff845f',
  '#5391fe',
  '#ab95ea',
  '#ffe9b3',
  '#6acaa5',
  '#f5b496',
];
const PERIOD_TYPE_OPTIONS = [
  {textKey: 'manager_overview_period_month', value: 'month'},
  {textKey: 'manager_overview_period_quarter', value: 'quarter'},
];
const PERIOD_STATE_STORAGE_PREFIX = 'manager_overview_period_state';
const ABC_CUSTOMER_LEVELS = ['A', 'B', 'C'];
const BASE_REPORT_CACHE = new Map();
const TEAM_DETAIL_CACHE = new Map();
const ANALYSIS_CACHE = new Map();

function uniq(values) {
  return Array.from(new Set((values || []).filter(Boolean).map(item => String(item))));
}

function percent(numerator, denominator) {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
}

function formatPercent(value) {
  return value === null || value === undefined ? '--' : `${value}%`;
}

function getMonthRange(offset = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  if (offset) {
    end.setFullYear(start.getFullYear(), start.getMonth() + 1, 1);
  }
  return {
    start,
    end,
    startIso: start.toISOString(),
    endIso: end.toISOString(),
    key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
    label: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
  };
}

function getQuarterRange(offset = 0) {
  const now = new Date();
  const currentQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
  const start = new Date(now.getFullYear(), currentQuarterStartMonth - (offset * 3), 1);
  const end = new Date(start.getFullYear(), start.getMonth() + 3, 1);
  const quarter = Math.floor(start.getMonth() / 3) + 1;
  return {
    start,
    end,
    startIso: start.toISOString(),
    endIso: end.toISOString(),
    key: `${start.getFullYear()}-Q${quarter}`,
    label: `${start.getFullYear()} Q${quarter}`,
  };
}

function buildPeriodOptions(type) {
  const count = type === 'quarter' ? 5 : 13;
  const rangeGetter = type === 'quarter' ? getQuarterRange : getMonthRange;
  return Array.from({length: count}, (_, index) => {
    const range = rangeGetter(index);
    return {
      text: range.label,
      value: range.key,
      range,
    };
  });
}

function getPeriodRange(type, key) {
  const options = buildPeriodOptions(type);
  const matched = options.find(item => item.value === key) || options[0];
  return matched.range;
}

function buildPeriodStateStorageKey({userId, territoryId, territoryType} = {}) {
  return [
    PERIOD_STATE_STORAGE_PREFIX,
    userId || 'unknown-user',
    territoryType || 'unknown-type',
    territoryId || 'unknown-territory',
  ].join(':');
}

function getInitialPeriodState(context = {}) {
  const fallbackType = 'month';
  const fallbackOptions = buildPeriodOptions(fallbackType);
  const fallbackKey = fallbackOptions[0]?.value || '';

  try {
    const raw = window.localStorage.getItem(buildPeriodStateStorageKey(context));
    const parsed = raw ? JSON.parse(raw) : null;
    const periodType = ['month', 'quarter'].includes(parsed?.periodType) ? parsed.periodType : fallbackType;
    const options = buildPeriodOptions(periodType);
    const periodKey = options.some(item => item.value === parsed?.periodKey)
      ? parsed.periodKey
      : (options[0]?.value || fallbackKey);
    return {periodType, periodKey};
  } catch (error) {
    return {periodType: fallbackType, periodKey: fallbackKey};
  }
}

function savePeriodState(context = {}, periodState = {}) {
  try {
    window.localStorage.setItem(
      buildPeriodStateStorageKey(context),
      JSON.stringify({
        periodType: periodState.periodType,
        periodKey: periodState.periodKey,
      }),
    );
  } catch (error) {
    // Ignore storage errors; the selected state still works for the current page instance.
  }
}

function cloneList(list = []) {
  return Array.isArray(list) ? list.slice() : [];
}

function isRpmRole(roleId, roleName) {
  return String(roleId || '') === '11' || String(roleName || '').toUpperCase() === 'RPM';
}

function toDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return '--';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatMonthDay(value) {
  const date = toDate(value);
  if (!date) return '--';
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatPeriodDateRange(range) {
  if (!range?.start || !range?.end) return '--';
  const inclusiveEnd = new Date(range.end.getTime());
  inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
  return `${formatMonthDay(range.start)}~${formatMonthDay(inclusiveEnd)}`;
}

function formatDurationSecondsValue(seconds) {
  const value = Math.round(Number(seconds) || 0);
  if (!value) return '--';
  if (value < 60) return `${value}秒`;
  const minutes = Math.floor(value / 60);
  const remainingSeconds = value % 60;
  return remainingSeconds ? `${minutes}分${remainingSeconds}秒` : `${minutes}分`;
}

function getStatQuarterKey(value) {
  const date = toDate(value);
  if (!date) return '';
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `${date.getFullYear()}Q${quarter}`;
}

function summarizeContentInteractionStats(rows = []) {
  const months = (rows || []).filter(Boolean);
  const targetSendCount = months.reduce((max, item) => Math.max(max, Number(item.target_send_count) || 0), 0);
  const sendCount = months.reduce((total, item) => total + (Number(item.send_count) || 0), 0);
  const openCount = months.reduce((total, item) => total + (Number(item.open_count) || 0), 0);
  return {
    targetSendCount,
    sendCount,
    openCount,
    progress: percent(sendCount, targetSendCount) || 0,
    months,
  };
}

function getCallId(call) {
  return String(call?.physician_mr_call_id || call?.source_msl_call_id || call?.call_id || call?.id || '');
}

function getPhysicianId(item) {
  return String(item?.physician_id || item?.receiver_physician_id || item?.reader_physician_id || item?.physician?.physician_id || item?.hcp_id || '');
}

function getCustomerLevel(item) {
  const level = String(item?.physician_segment || '').trim().toUpperCase();
  return ABC_CUSTOMER_LEVELS.includes(level) ? level : '';
}

function getDurationSeconds(item) {
  const value = Number(item?.duration ?? item?.view_duration ?? item?.read_duration ?? item?.watch_duration ?? 0);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function getPhysicianName(item) {
  return item?.physician_name || item?.hcp_name || item?.physician?.physician_name || getPhysicianId(item) || '未知客户';
}

function getProductId(item) {
  return String(item?.product_id || item?.product_name || 'unknown');
}

function getProductName(item) {
  return item?.product_name || item?.product_id || '未知产品';
}

function normalizeTypingId(value) {
  const typing = String(value || '').trim();
  return typing || UNKNOWN_TYPING_ID;
}

function getPhysicianTyping(item) {
  return normalizeTypingId(
    item?.physician_typing ||
    item?.physician_typing_code ||
    item?.physician_typing_id ||
    item?.typing_id ||
    item?.typing_code ||
    item?.physician_segment ||
    item?.segment ||
    item?.segment_id ||
    item?.segment_code,
  );
}

function getTypingDefinitionId(item) {
  return String(
    item?.cnf_physician_segment_id ||
    item?.physician_typing ||
    item?.physician_typing_code ||
    item?.physician_typing_id ||
    item?.typing_id ||
    item?.typing_code ||
    item?.physician_segment ||
    item?.segment ||
    item?.segment_id ||
    item?.segment_code ||
    item?.code ||
    item?.value,
  ).trim();
}

function getTypingDefinitionAliases(item) {
  return uniq([
    item?.cnf_physician_segment_id,
    item?.physician_typing,
    item?.physician_typing_code,
    item?.physician_typing_id,
    item?.typing_id,
    item?.typing_code,
    item?.physician_segment,
    item?.segment,
    item?.segment_id,
    item?.segment_code,
    item?.segment_name,
    item?.code,
    item?.value,
  ]);
}

function getTypingDefinitionProductId(item) {
  return String(item?.product_id || item?.product_name || '').trim();
}

function getTypingDefinitionName(item) {
  return String(
    item?.segment_name ||
    item?.physician_typing_name ||
    item?.physician_typing_label ||
    item?.typing_name ||
    item?.typing_label ||
    item?.physician_segment_name ||
    item?.segment_label ||
    item?.name ||
    item?.label ||
    item?.display_name ||
    item?.physician_typing ||
    UNKNOWN_TYPING_NAME,
  ).trim() || UNKNOWN_TYPING_NAME;
}

function getTypingDefinitionOrder(item, fallbackOrder) {
  const order = Number(
    item?.sort_order ??
    item?.display_order ??
    item?.sequence ??
    item?.order_no ??
    item?.sort ??
    item?.seq,
  );
  return Number.isFinite(order) ? order : fallbackOrder;
}

function buildProductTypingKey(productId, typingId) {
  return `${String(productId || '').trim()}__${String(typingId || '').trim()}`;
}

function getStatOwner(item) {
  const id = item?.call_user_id || item?.sender_user_id || item?.user_id || item?.owner_user_id || item?.mr_user_id || '';
  const name = item?.call_user_name || item?.sender_user_name || item?.user_name || item?.owner_name || item?.mr_user_name || '';
  return {id: String(id || name || 'unknown'), name: name || '未知成员'};
}

function getTaskOwner(task) {
  const id = task?.user_id_mr || task?.mr_user_id || task?.user_id || task?.owner_user_id || task?.execute_user_id || '';
  const name = task?.user_name_mr || task?.mr_user_name || task?.user_name || task?.owner_name || task?.execute_user_name || '';
  return {id: String(id || name || 'unknown'), name: name || '未知成员'};
}

function getTaskTerritoryId(task) {
  return String(task?.territory_id || task?.call_territory_id || task?.submit_territory_id || task?.sender_territory_id || '');
}

function isNbaAccepted(item) {
  const status = String(item?.feedback_status || item?.status || '').toLowerCase();
  return status === 'accepted' || status === 'accept';
}

function isNbaRejected(item) {
  const status = String(item?.feedback_status || item?.status || '').toLowerCase();
  return status === 'rejected' || status === 'reject' || status === 'ignored' || status === 'ignore';
}

function getTerritoryOwner(territory) {
  const id = territory?.user_id || territory?.territory_id || '';
  const name = territory?.user_name || territory?.territory_name || '未知成员';
  return {id: String(id || name || 'unknown'), name};
}

function getTerritoryType(territory) {
  return String(territory?.territory_type || '').toUpperCase();
}

function isAreaTerritory(territory) {
  return getTerritoryType(territory) === 'AM';
}

function isDistrictTerritory(territory) {
  return getTerritoryType(territory) === 'DM';
}

export default {
  name: 'TeamReport',
  components: {
    [ActionSheet.name]: ActionSheet,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Empty.name]: Empty,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    UserInfoPopup,
  },
  props: {
    reportMode: {
      type: String,
      default: 'team',
    },
    hideTaskAndNba: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const userId = getLocalStorage('user_id') || '';
    const territoryId = getLocalStorage('territory_id') || '';
    const roleId = getLocalStorage('role_id') || '';
    const currentRole = getLocalStorage('role_name') || '';
    const territoryType = isRpmRole(roleId, currentRole) ? 'AM' : (getLocalStorage('territory_type') || '');
    const periodState = getInitialPeriodState({userId, territoryId, territoryType});
    const periodType = periodState.periodType;
    const periodOptions = buildPeriodOptions(periodType);
    const periodKey = periodOptions.some(item => item.value === periodState.periodKey)
      ? periodState.periodKey
      : (periodOptions[0]?.value || '');
    const monthRange = getPeriodRange(periodType, periodKey);
    return {
      analysisLoading: false,
      callStats: [],
      contentOpenStats: [],
      contentSendStats: [],
      coverageStats: [],
      currentRole,
      feedbackStats: [],
      detailRecentCallError: '',
      detailRecentCalls: [],
      detailRecentCallsLoading: false,
      detailRecentRequestId: 0,
      loading: true,
      loadingProgress: {
        completedModules: 0,
        currentLabel: '',
        expectedMembers: 0,
        fetchedRows: 0,
        percent: 0,
        totalModules: 1,
      },
      loadRequestId: 0,
      loadErrors: [],
      memberPromotionStatsByKey: {},
      memberPromotionLoadingKey: '',
      memberPromotionErrorKey: '',
      memberContentDetailVisible: false,
      memberContentDetailLoading: false,
      memberContentDetailError: '',
      memberContentDetailFilter: 'all',
      memberContentDetailRows: [],
      memberContentDetailRequestId: 0,
      memberDetailVisible: false,
      monthRange,
      abcTargetCustomerStats: [],
      abcTargetProductStats: [],
      taskStats: [],
      targetCustomerStats: [],
      nbaFeedbackStats: [],
      nbaSuggestionStats: [],
      periodKey,
      periodType,
      physicianTypingDefinitions: [],
      physicianTypingDefinitionsLoaded: false,
      productSegmentStats: [],
      productSegmentLoaded: false,
      productSegmentScopeKey: '',
      selectedAreaTerritoryId: '',
      selectedDistrictTerritoryId: '',
      selectedMemberId: '',
      subordinateTerritories: [],
      targetProductStats: [],
      targetProductScopeKey: '',
      teamDetailLoaded: false,
      teamDetailLoading: false,
      teamDetailRequestId: 0,
      teamScrollTop: 0,
      territoryHierarchy: [],
      abcCoveredTargetCustomerStats: [],
      abcCoveredTargetProductStats: [],
      coveredTargetCustomerStats: [],
      coveredTargetProductStats: [],
      territoryId,
      territoryType,
      userId,
      userName: getLocalStorage('user_name') || '',
      roleId,
      roleActions: [],
      showRoleSelector: false,
    };
  },
  computed: {
    isManagerRole() {
      return MANAGER_ROLES.includes(this.effectiveTerritoryType);
    },
    isRpmRole() {
      return isRpmRole(this.roleId, this.currentRole);
    },
    effectiveTerritoryType() {
      return this.isRpmRole ? 'AM' : String(this.territoryType || '').toUpperCase();
    },
    periodTypeOptions() {
      return PERIOD_TYPE_OPTIONS.map(item => ({
        text: this.$t(item.textKey),
        value: item.value,
      }));
    },
    normalizedReportMode() {
      return String(this.reportMode || '').toLowerCase() === 'overview' ? 'overview' : 'team';
    },
    isOverviewReport() {
      return this.normalizedReportMode === 'overview';
    },
    isTeamReport() {
      return this.normalizedReportMode === 'team';
    },
    pageTitle() {
      return this.isOverviewReport
        ? this.$t('manager_overview_tab_overview')
        : this.$t('manager_overview_team');
    },
    isSalesDirectorRole() {
      return this.effectiveTerritoryType === 'SD';
    },
    isAreaManagerRole() {
      return this.effectiveTerritoryType === 'AM';
    },
    roleLabel() {
      if (this.isRpmRole) return 'RPM';
      return this.effectiveTerritoryType || this.$t('role_sales');
    },
    scopeLabel() {
      const type = this.effectiveTerritoryType;
      const displayName = this.currentTerritoryName || this.territoryId;
      if (type === 'SD') return this.$t('manager_overview_scope_national');
      if (!displayName) return '';
      if (type === 'AM') return this.$t('manager_overview_scope_area', {scope: displayName});
      if (type === 'DM') return this.$t('manager_overview_scope_district', {scope: displayName});
      return displayName;
    },
    currentTerritoryName() {
      const territoryId = String(this.territoryId || '');
      if (!territoryId) return '';
      const territory = this.territoryByIdMap.get(territoryId);
      return territory?.territory_name || '';
    },
    currentRoleDisplay() {
      return this.getRoleDisplayName(this.currentRole);
    },
    periodOptions() {
      return buildPeriodOptions(this.periodType);
    },
    periodRangeText() {
      return formatPeriodDateRange(this.monthRange);
    },
    loadingCurrentModuleIndex() {
      const total = Math.max(Number(this.loadingProgress.totalModules) || 1, 1);
      return Math.min((Number(this.loadingProgress.completedModules) || 0) + 1, total);
    },
    submittedCallCount() {
      return this.sumStats(this.callStats, 'call_count');
    },
    feedbackCallCount() {
      return this.sumStats(this.feedbackStats, 'feedback_call_count');
    },
    coveredCustomerCount() {
      const source = this.coveredTargetCustomerStats.length
        ? this.coveredTargetCustomerStats
        : this.coveredTargetProductStats;
      return uniq(source.map(getPhysicianId)).length;
    },
    targetCustomerCount() {
      const source = this.targetCustomerStats.length
        ? this.targetCustomerStats
        : this.targetProductStats;
      const physicianIds = uniq(source.map(getPhysicianId));
      return physicianIds.length || this.sumStats(this.targetProductStats, 'target_customer_count');
    },
    coverageRateText() {
      return formatPercent(percent(this.coveredCustomerCount, this.targetCustomerCount));
    },
    abcCoveredCustomerCount() {
      const source = this.abcCoveredTargetCustomerStats.length
        ? this.abcCoveredTargetCustomerStats
        : this.abcCoveredTargetProductStats;
      return uniq(source.map(getPhysicianId)).length;
    },
    abcTargetCustomerCount() {
      const source = this.abcTargetCustomerStats.length
        ? this.abcTargetCustomerStats
        : this.abcTargetProductStats;
      const physicianIds = uniq(source.map(getPhysicianId));
      return physicianIds.length || this.sumStats(this.abcTargetProductStats, 'target_customer_count');
    },
    abcCoverageRateText() {
      return formatPercent(percent(this.abcCoveredCustomerCount, this.abcTargetCustomerCount));
    },
    abcCoverageRows() {
      return ABC_CUSTOMER_LEVELS.map((level) => {
        const targetCustomerIds = uniq(this.abcTargetCustomerStats
          .filter(item => getCustomerLevel(item) === level)
          .map(getPhysicianId));
        const coveredCustomerIds = uniq(this.abcCoveredTargetCustomerStats
          .filter(item => getCustomerLevel(item) === level)
          .map(getPhysicianId));
        const targetCustomerCount = targetCustomerIds.length;
        const coveredCustomerCount = coveredCustomerIds.length;
        const coverageRate = percent(coveredCustomerCount, targetCustomerCount);
        return {
          level,
          targetCustomerCount,
          coveredCustomerCount,
          coverageRate,
          coverageRateText: formatPercent(coverageRate),
        };
      });
    },
    contentSendCount() {
      return this.sumStats(this.contentSendStats, 'send_count');
    },
    contentReachedCustomerCount() {
      return uniq(this.contentSendStats.map(getPhysicianId)).length;
    },
    targetPhysicianIdSet() {
      return new Set(this.targetCustomerStats.map(getPhysicianId).filter(Boolean));
    },
    targetContentOpenStats() {
      const targetIds = this.targetPhysicianIdSet;
      if (!targetIds.size) return [];
      return this.contentOpenStats.filter(item => targetIds.has(getPhysicianId(item)));
    },
    contentTargetOpenCustomerCount() {
      return uniq(this.targetContentOpenStats.map(getPhysicianId)).length;
    },
    contentTargetOpenCount() {
      return this.targetContentOpenStats.reduce((total, item) => total + (this.getStatCount(item, 'open_count') || 0), 0);
    },
    contentAverageOpenDuration() {
      const summary = this.targetContentOpenStats.reduce((total, item) => {
        const duration = getDurationSeconds(item);
        const openCount = this.getStatCount(item, 'open_count') || 0;
        if (!duration || !openCount) return total;
        total.duration += duration * openCount;
        total.count += openCount;
        return total;
      }, {duration: 0, count: 0});
      if (!summary.count) return 0;
      return summary.duration / summary.count;
    },
    contentAverageOpenDurationText() {
      return formatDurationSecondsValue(this.contentAverageOpenDuration);
    },
    subordinateTerritoryIds() {
      return uniq(this.subordinateTerritories.map(item => item.territory_id));
    },
    subordinateUserIds() {
      return uniq(this.subordinateTerritories.map(item => item.user_id));
    },
    subordinateUserNames() {
      return uniq(this.subordinateTerritories.map(item => item.user_name));
    },
    territoryByIdMap() {
      const map = new Map();
      this.territoryHierarchy.forEach((territory) => {
        const id = String(territory?.territory_id || '');
        if (id) map.set(id, territory);
      });
      return map;
    },
    areaOptions() {
      const options = [{text: this.$t('manager_overview_all_areas'), value: ''}];
      this.territoryHierarchy
        .filter(isAreaTerritory)
        .sort((a, b) => String(a.territory_name || a.territory_id).localeCompare(String(b.territory_name || b.territory_id)))
        .forEach((territory) => {
          options.push({
            text: territory.territory_name || territory.territory_id,
            value: territory.territory_id,
          });
        });
      return options;
    },
    availableDistrictTerritories() {
      return this.territoryHierarchy
        .filter((territory) => {
          if (!isDistrictTerritory(territory)) return false;
          if (!this.selectedAreaTerritoryId) return true;
          return this.getAncestorTerritory(territory, isAreaTerritory)?.territory_id === this.selectedAreaTerritoryId;
        })
        .sort((a, b) => String(a.territory_name || a.territory_id).localeCompare(String(b.territory_name || b.territory_id)));
    },
    districtMemberCountById() {
      const map = new Map();
      this.memberRows.forEach((member) => {
        if (this.selectedAreaTerritoryId && member.areaTerritoryId !== this.selectedAreaTerritoryId) return;
        const districtId = String(member.districtTerritoryId || '');
        if (!districtId) return;
        map.set(districtId, (map.get(districtId) || 0) + 1);
      });
      return map;
    },
    districtOptions() {
      const totalCount = this.availableDistrictTerritories.reduce((total, territory) => (
        total + (this.districtMemberCountById.get(String(territory.territory_id || '')) || 0)
      ), 0);
      return [
        {text: this.$t('manager_overview_all_districts'), value: '', count: totalCount},
        ...this.availableDistrictTerritories.map(territory => ({
          text: territory.territory_name || territory.territory_id,
          value: territory.territory_id,
          count: this.districtMemberCountById.get(String(territory.territory_id || '')) || 0,
        })),
      ];
    },
    selectedDistrictOptionTitle() {
      const selected = this.districtOptions.find(item => item.value === this.selectedDistrictTerritoryId);
      return selected?.text || this.$t('manager_overview_all_districts');
    },
    showMemberTerritoryFilters() {
      if (this.isSalesDirectorRole) return this.areaOptions.length > 1 || this.districtOptions.length > 1;
      if (this.isAreaManagerRole) return this.districtOptions.length > 1;
      return false;
    },
    showAreaMemberFilter() {
      return this.isSalesDirectorRole && this.areaOptions.length > 1;
    },
    showDistrictMemberFilter() {
      return (this.isSalesDirectorRole || this.isAreaManagerRole) && this.districtOptions.length > 1;
    },
    memberTerritoryScopeMap() {
      const map = new Map();
      this.subordinateTerritories.forEach((territory) => {
        const territoryId = String(territory?.territory_id || '');
        if (!territoryId) return;
        const area = this.getAncestorTerritory(territory, isAreaTerritory);
        const district = this.getAncestorTerritory(territory, isDistrictTerritory);
        map.set(territoryId, {
          areaTerritoryId: area?.territory_id || '',
          areaTerritoryName: area?.territory_name || '',
          districtTerritoryId: district?.territory_id || '',
          districtTerritoryName: district?.territory_name || '',
        });
      });
      return map;
    },
    nbaExecutedCount() {
      return this.nbaFeedbackStats
        .filter(isNbaAccepted)
        .reduce((total, item) => total + this.getStatCount(item, 'feedback_count'), 0);
    },
    nbaIgnoredCount() {
      return this.nbaFeedbackStats
        .filter(isNbaRejected)
        .reduce((total, item) => total + this.getStatCount(item, 'feedback_count'), 0);
    },
    nbaTotalCount() {
      return this.sumStats(this.nbaSuggestionStats, 'suggestion_count') || (this.nbaExecutedCount + this.nbaIgnoredCount);
    },
    taskExecutedCount() {
      return this.taskStats.reduce((total, item) => (
        total + this.getStatCount(item, 'accept_no') + this.getStatCount(item, 'ignore_no')
      ), 0);
    },
    taskTotalCount() {
      return this.sumStats(this.taskStats, 'task_no');
    },
    summaryMetrics() {
      const metrics = [
        {
          label: this.$t('manager_overview_calls'),
          value: this.submittedCallCount,
          desc: this.$t('manager_overview_feedback_desc', {
            count: this.feedbackCallCount,
            rate: formatPercent(percent(this.feedbackCallCount, this.submittedCallCount)),
          }),
        },
        {
          label: this.$t('manager_overview_content_open_send'),
          value: `${this.contentTargetOpenCount}/${this.contentSendCount || '--'}`,
          desc: this.$t('manager_overview_content_reach_avg_desc', {
            reached: this.contentReachedCustomerCount,
            duration: this.contentAverageOpenDurationText,
          }),
        },
      ];
      if (!this.hideTaskAndNba) {
        metrics.push({
          label: this.$t('manager_overview_task_execution'),
          value: `${this.taskExecutedCount}/${this.taskTotalCount || '--'}`,
          desc: this.$t('manager_overview_execution_rate_desc', {rate: formatPercent(percent(this.taskExecutedCount, this.taskTotalCount))}),
        });
        metrics.push({
          label: this.$t('manager_overview_nba_execution'),
          value: this.nbaExecutedCount,
          desc: this.$t('manager_overview_total_suggestions_desc', {count: this.nbaTotalCount || '--'}),
        });
      }
      return metrics;
    },
    qualityMetrics() {
      const feedbackRate = percent(this.feedbackCallCount, this.submittedCallCount);
      const nbaRate = percent(this.nbaExecutedCount + this.nbaIgnoredCount, this.nbaTotalCount);
      const taskRate = percent(this.taskExecutedCount, this.taskTotalCount);
      const metrics = [
        {
          label: this.$t('manager_overview_call_feedback_completed'),
          value: formatPercent(feedbackRate),
          progress: feedbackRate || 0,
          desc: this.$t('manager_overview_call_feedback_completed_desc', {count: this.feedbackCallCount}),
        },
      ];
      if (!this.hideTaskAndNba) {
        metrics.push({
          label: this.$t('manager_overview_task_execution_completed'),
          value: formatPercent(taskRate),
          progress: taskRate || 0,
          desc: this.$t('manager_overview_task_execution_completed_desc', {
            executed: this.taskExecutedCount,
            total: this.taskTotalCount,
          }),
        });
        metrics.push({
          label: this.$t('manager_overview_nba_process_completed'),
          value: formatPercent(nbaRate),
          progress: nbaRate || 0,
          desc: this.$t('manager_overview_nba_process_completed_desc', {
            accepted: this.nbaExecutedCount,
            rejected: this.nbaIgnoredCount,
          }),
        });
      }
      metrics.push({
        label: this.$t('manager_overview_content_open_send'),
        value: `${this.contentTargetOpenCount}/${this.contentSendCount || '--'}`,
        progress: percent(this.contentTargetOpenCount, this.contentSendCount) || 0,
        desc: this.$t('manager_overview_content_reach_avg_desc', {
          reached: this.contentReachedCustomerCount,
          duration: this.contentAverageOpenDurationText,
        }),
      });
      return metrics;
    },
    typingDefinitionRows() {
      const definitions = new Map();
      (this.physicianTypingDefinitions || []).forEach((item, index) => {
        const id = getTypingDefinitionId(item);
        const productId = getTypingDefinitionProductId(item);
        const key = buildProductTypingKey(productId, id);
        if (!id || !productId || definitions.has(key)) return;
        definitions.set(key, {
          id,
          productId,
          aliases: getTypingDefinitionAliases(item),
          name: getTypingDefinitionName(item),
          order: getTypingDefinitionOrder(item, index),
        });
      });
      return Array.from(definitions.values())
        .sort((a, b) => a.productId.localeCompare(b.productId) || a.order - b.order || a.name.localeCompare(b.name))
        .map((item) => ({
          ...item,
          color: SEGMENT_BAR_COLORS[(Math.max(item.order, 1) - 1) % SEGMENT_BAR_COLORS.length],
        }));
    },
    typingDefinitionMap() {
      const map = new Map();
      this.typingDefinitionRows.forEach((item) => {
        item.aliases.forEach((alias) => {
          map.set(buildProductTypingKey(item.productId, alias), item);
        });
      });
      return map;
    },
    productNameMap() {
      const map = new Map();
      this.productSegmentStats.forEach((item) => {
        const id = getProductId(item);
        const name = getProductName(item);
        if (id && name && name !== '未知产品') map.set(id, name);
      });
      return map;
    },
    productSegmentRows() {
      const products = new Map();
      const ensureProduct = (item = {}) => {
        const id = getProductId(item);
        if (!products.has(id)) {
          products.set(id, {
            id,
            name: this.getDisplayProductName(item),
            targetCustomerCount: 0,
            segments: new Map(),
          });
        }
        return products.get(id);
      };
      const ensureSegment = (product, item = {}) => {
        const rawTypingId = getPhysicianTyping(item);
        const definition = this.typingDefinitionMap.get(buildProductTypingKey(product.id, rawTypingId));
        const typingId = definition ? definition.id : UNKNOWN_TYPING_ID;
        if (!product.segments.has(typingId)) {
          product.segments.set(typingId, {
            id: typingId,
            name: definition?.name || this.$t('manager_overview_other'),
            order: definition?.order ?? Number.MAX_SAFE_INTEGER,
            color: definition?.color || UNKNOWN_TYPING_COLOR,
            targetCustomerCount: 0,
          });
        }
        return product.segments.get(typingId);
      };

      this.productSegmentStats.forEach((item) => {
        const count = this.getStatCount(item, 'target_customer_count');
        if (!count) return;
        const product = ensureProduct(item);
        const segment = ensureSegment(product, item);
        product.targetCustomerCount += count;
        segment.targetCustomerCount += count;
      });

      return Array.from(products.values())
        .map((product) => {
          const targetCustomerCount = product.targetCustomerCount;
          const segments = Array.from(product.segments.values())
            .sort((a, b) => {
              return a.order - b.order || a.name.localeCompare(b.name);
            })
            .map((segment) => {
              const segmentTargetCustomerCount = segment.targetCustomerCount;
              const sharePercent = targetCustomerCount ? (segmentTargetCustomerCount / targetCustomerCount) * 100 : 0;
              const shareText = formatPercent(percent(segmentTargetCustomerCount, targetCustomerCount));
              const labelText = this.$t('manager_overview_segment_label', {
                segment: segment.name,
                count: segmentTargetCustomerCount,
                share: shareText,
              });
              const shortLabelText = `${segment.name} ${segmentTargetCustomerCount} ${shareText}`;
              return {
                id: segment.id,
                name: segment.name,
                order: segment.order,
                targetCustomerCount: segmentTargetCustomerCount,
                sharePercent,
                shareText,
                widthStyle: `${sharePercent}%`,
                color: segment.color,
                labelText,
                shortLabelText,
              };
            });
          return {
            id: product.id,
            name: product.name,
            targetCustomerCount,
            segments,
          };
        })
        .sort((a, b) => b.targetCustomerCount - a.targetCustomerCount);
    },
    memberRows() {
      const members = new Map();
      const ensureMember = (owner, source = {}) => {
        const id = owner.id || owner.name || 'unknown';
        if (!members.has(id)) {
          members.set(id, {
            id,
            userId: owner.id || source.user_id || source.call_user_id || source.submit_user_id || '',
            name: owner.name || this.$t('manager_overview_unknown_member'),
            territoryId: source.territory_id || source.call_territory_id || source.submit_territory_id || '',
            territoryName: source.territory_name || '',
            callCount: 0,
            contentSendCount: 0,
            feedbackCount: 0,
            nbaExecuted: 0,
            nbaIgnored: 0,
            nbaTotal: 0,
            taskExecuted: 0,
            taskTotal: 0,
            contentReceiverIds: new Set(),
            contentOpenCustomerIds: new Set(),
            contentOpenCount: 0,
            contentOpenDurationCount: 0,
            contentOpenDurationSum: 0,
            contentOpenTargetCustomerIds: new Set(),
            coveredCustomerIds: new Set(),
            abcCoveredTargetCustomerIds: new Set(),
            coveredTargetCustomerIds: new Set(),
            productStats: new Map(),
            abcTargetCustomerIds: new Set(),
            abcTargetCustomerIdsByLevel: ABC_CUSTOMER_LEVELS.reduce((result, level) => ({
              ...result,
              [level]: new Set(),
            }), {}),
            abcCoveredTargetCustomerIdsByLevel: ABC_CUSTOMER_LEVELS.reduce((result, level) => ({
              ...result,
              [level]: new Set(),
            }), {}),
            targetCustomerIds: new Set(),
          });
        }
        const member = members.get(id);
        if (!member.userId) member.userId = owner.id || source.user_id || source.call_user_id || source.submit_user_id || '';
        if (!member.territoryId) member.territoryId = source.territory_id || source.call_territory_id || source.submit_territory_id || '';
        if (!member.territoryName) member.territoryName = source.territory_name || '';
        return member;
      };
      const ensureProduct = (member, source = {}) => {
        const productId = getProductId(source);
        if (!member.productStats.has(productId)) {
          member.productStats.set(productId, {
            id: productId,
            name: this.productNameMap.get(productId) || this.getDisplayProductName(source),
            targetCount: 0,
            targetPhysicianIds: new Set(),
            coveredPhysicianIds: new Set(),
          });
        }
        return member.productStats.get(productId);
      };

      this.subordinateTerritories.forEach((territory) => {
        const member = ensureMember(getTerritoryOwner(territory), territory);
        member.territoryId = territory.territory_id || member.territoryId;
        member.territoryName = territory.territory_name || member.territoryName;
      });

      this.callStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        member.callCount += this.getStatCount(stat, 'call_count');
      });

      this.feedbackStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        member.feedbackCount += this.getStatCount(stat, 'feedback_call_count');
      });

      this.contentSendStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        member.contentSendCount += this.getStatCount(stat, 'send_count');
        if (physicianId) member.contentReceiverIds.add(physicianId);
      });

      this.targetCustomerStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) member.targetCustomerIds.add(physicianId);
      });

      this.contentOpenStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (!physicianId) return;
        const duration = getDurationSeconds(stat);
        const openCount = this.getStatCount(stat, 'open_count') || 0;
        member.contentOpenCount += openCount;
        member.contentOpenCustomerIds.add(physicianId);
        if (member.targetCustomerIds.has(physicianId)) {
          member.contentOpenTargetCustomerIds.add(physicianId);
        }
        if (duration && openCount) {
          member.contentOpenDurationSum += duration * openCount;
          member.contentOpenDurationCount += openCount;
        }
      });

      this.abcTargetCustomerStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        const level = getCustomerLevel(stat);
        if (physicianId) {
          member.abcTargetCustomerIds.add(physicianId);
          if (level) member.abcTargetCustomerIdsByLevel[level].add(physicianId);
        }
      });

      this.coveredTargetCustomerStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) member.coveredTargetCustomerIds.add(physicianId);
      });

      this.abcCoveredTargetCustomerStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        const level = getCustomerLevel(stat);
        if (physicianId) {
          member.abcCoveredTargetCustomerIds.add(physicianId);
          if (level) member.abcCoveredTargetCustomerIdsByLevel[level].add(physicianId);
        }
      });

      this.targetProductStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const product = ensureProduct(member, stat);
        const physicianId = getPhysicianId(stat);
        product.targetCount += this.getStatCount(stat, 'target_customer_count');
        if (physicianId) {
          product.targetPhysicianIds.add(physicianId);
          member.targetCustomerIds.add(physicianId);
        }
      });

      this.abcTargetProductStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) member.abcTargetCustomerIds.add(physicianId);
      });

      this.coveredTargetProductStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const product = ensureProduct(member, stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) {
          product.coveredPhysicianIds.add(physicianId);
          member.coveredTargetCustomerIds.add(physicianId);
        }
      });

      this.abcCoveredTargetProductStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) member.abcCoveredTargetCustomerIds.add(physicianId);
      });

      this.coverageStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const physicianId = getPhysicianId(stat);
        if (physicianId) member.coveredCustomerIds.add(physicianId);
      });

      this.taskStats.forEach((taskStat) => {
        const member = ensureMember(getTaskOwner(taskStat), taskStat);
        member.taskTotal += this.getStatCount(taskStat, 'task_no');
        member.taskExecuted += this.getStatCount(taskStat, 'accept_no') + this.getStatCount(taskStat, 'ignore_no');
      });

      this.nbaSuggestionStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        member.nbaTotal += this.getStatCount(stat, 'suggestion_count');
      });

      this.nbaFeedbackStats.forEach((stat) => {
        const member = ensureMember(getStatOwner(stat), stat);
        const count = this.getStatCount(stat, 'feedback_count');
        if (isNbaAccepted(stat)) member.nbaExecuted += count;
        if (isNbaRejected(stat)) member.nbaIgnored += count;
      });

      return Array.from(members.values())
        .map((member) => {
          const territoryScope = this.memberTerritoryScopeMap.get(member.territoryId) || {};
          const productRows = Array.from(member.productStats.values())
            .map((product) => {
              const targetCustomerCount = product.targetPhysicianIds.size || product.targetCount;
              const coveredCustomerCount = product.coveredPhysicianIds.size;
              const coverageRate = percent(coveredCustomerCount, targetCustomerCount);
              return {
                id: product.id,
                name: product.name,
                targetCustomerCount,
                coveredCustomerCount,
                coverageRate,
                coverageRateText: formatPercent(coverageRate),
              };
            })
            .sort((a, b) => b.targetCustomerCount - a.targetCustomerCount || b.coveredCustomerCount - a.coveredCustomerCount);
          const productTargetCustomerCount = productRows.reduce((total, product) => total + product.targetCustomerCount, 0);
          const productCoveredCustomerCount = productRows.reduce((total, product) => total + product.coveredCustomerCount, 0);
          const targetCustomerCount = member.targetCustomerIds.size || productTargetCustomerCount;
          const coveredCustomerCount = member.coveredTargetCustomerIds.size || productCoveredCustomerCount;
          const coverageRate = percent(coveredCustomerCount, targetCustomerCount);
          const abcTargetCustomerCount = member.abcTargetCustomerIds.size;
          const abcCoveredCustomerCount = member.abcCoveredTargetCustomerIds.size;
          const abcCoverageRate = percent(abcCoveredCustomerCount, abcTargetCustomerCount);
          const abcCoverageRows = ABC_CUSTOMER_LEVELS.map((level) => {
            const targetLevelCount = member.abcTargetCustomerIdsByLevel[level].size;
            const coveredLevelCount = member.abcCoveredTargetCustomerIdsByLevel[level].size;
            const levelCoverageRate = percent(coveredLevelCount, targetLevelCount);
            return {
              level,
              targetCustomerCount: targetLevelCount,
              coveredCustomerCount: coveredLevelCount,
              coverageRate: levelCoverageRate,
              coverageRateText: formatPercent(levelCoverageRate),
            };
          });
          const contentAverageOpenDuration = member.contentOpenDurationCount
            ? member.contentOpenDurationSum / member.contentOpenDurationCount
            : 0;
          const productSummaryText = productRows
            .slice(0, 2)
            .map(product => `${product.name} ${product.coveredCustomerCount}/${product.targetCustomerCount}`)
            .join(' · ');
          return {
            ...member,
            ...territoryScope,
            productRows,
            productSummaryText,
            targetCustomerCount,
            targetCustomerCountText: targetCustomerCount || '--',
            coveredCustomerCount,
            coverageRate,
            coverageRateText: formatPercent(coverageRate),
            abcTargetCustomerCount,
            abcTargetCustomerCountText: abcTargetCustomerCount || '--',
            abcCoveredCustomerCount,
            abcCoverageRate,
            abcCoverageRateText: formatPercent(abcCoverageRate),
            abcCoverageRows,
            feedbackRateText: formatPercent(percent(member.feedbackCount, member.callCount)),
            contentTargetOpenCustomerCount: member.contentOpenTargetCustomerIds.size,
            contentOpenCustomerCount: member.contentOpenCustomerIds.size,
            contentOpenCount: member.contentOpenCount,
            contentAverageOpenDuration,
            contentAverageOpenDurationText: formatDurationSecondsValue(contentAverageOpenDuration),
            nbaRateText: formatPercent(percent(member.nbaExecuted + member.nbaIgnored, member.nbaTotal)),
            taskExecutionRateText: formatPercent(percent(member.taskExecuted, member.taskTotal)),
            taskTotalText: member.taskTotal || '--',
          };
        })
        .sort((a, b) => b.callCount - a.callCount || b.nbaExecuted - a.nbaExecuted);
    },
    filteredMemberRows() {
      return this.memberRows.filter((member) => {
        if (this.selectedAreaTerritoryId && member.areaTerritoryId !== this.selectedAreaTerritoryId) return false;
        if (this.selectedDistrictTerritoryId && member.districtTerritoryId !== this.selectedDistrictTerritoryId) return false;
        return true;
      });
    },
    selectedMember() {
      return this.filteredMemberRows.find(item => item.id === this.selectedMemberId) || this.filteredMemberRows[0] || null;
    },
    memberContentOpenedCount() {
      return this.memberContentDetailRows.filter(item => item.opened).length;
    },
    memberContentUnopenedCount() {
      return this.memberContentDetailRows.filter(item => !item.opened).length;
    },
    memberContentFilterOptions() {
      return [
        {value: 'all', label: this.$t('manager_content_all'), count: this.memberContentDetailRows.length},
        {value: 'opened', label: this.$t('manager_content_opened'), count: this.memberContentOpenedCount},
        {value: 'unopened', label: this.$t('manager_content_unopened'), count: this.memberContentUnopenedCount},
      ];
    },
    filteredMemberContentDetailRows() {
      if (this.memberContentDetailFilter === 'opened') {
        return this.memberContentDetailRows.filter(item => item.opened);
      }
      if (this.memberContentDetailFilter === 'unopened') {
        return this.memberContentDetailRows.filter(item => !item.opened);
      }
      return this.memberContentDetailRows;
    },
    detailMetricPeriodLabel() {
      if (this.periodType === 'quarter') return this.promotionQuarterKey;
      return this.monthRange?.key || this.periodKey || '';
    },
    promotionQuarterKey() {
      return getStatQuarterKey(this.monthRange?.start);
    },
    selectedMemberPromotionCacheKey() {
      const member = this.selectedMember;
      const userId = member?.userId || member?.id || '';
      return userId && this.promotionQuarterKey ? `${this.promotionQuarterKey}|${userId}` : '';
    },
    selectedMemberPromotionStats() {
      return this.selectedMemberPromotionCacheKey
        ? this.memberPromotionStatsByKey[this.selectedMemberPromotionCacheKey] || null
        : null;
    },
    selectedMemberPromotionLoading() {
      return Boolean(this.selectedMemberPromotionCacheKey && this.memberPromotionLoadingKey === this.selectedMemberPromotionCacheKey);
    },
    selectedMemberPromotionError() {
      return Boolean(this.selectedMemberPromotionCacheKey && this.memberPromotionErrorKey === this.selectedMemberPromotionCacheKey);
    },
    selectedPeriodIsCurrent() {
      if (this.periodType === 'quarter') return this.periodKey === getQuarterRange(0).key;
      return this.periodKey === getMonthRange(0).key;
    },
    selectedMemberPromotionNote() {
      const note = this.$t('manager_overview_online_promotion_note');
      if (!this.selectedPeriodIsCurrent) return note;
      return `${note}${this.$t('manager_overview_online_promotion_current_note')}`;
    },
    selectedMemberMetrics() {
      if (!this.selectedMember) return [];
      const metric = (label, value, unit = '') => ({label, value, unit});
      const valueText = (value) => {
        const text = String(value ?? '');
        return text || '--';
      };
      const withUnit = (label, value, unitKey) => {
        const text = valueText(value);
        return metric(label, text, text !== '--' ? this.$t(unitKey) : '');
      };
      const withPercent = (label, value) => {
        const text = valueText(value);
        if (text === '--') return metric(label, text);
        return text.endsWith('%')
          ? metric(label, text.slice(0, -1), '%')
          : metric(label, text);
      };
      const metrics = [
        withUnit(this.$t('manager_overview_target_customers'), this.selectedMember.targetCustomerCountText, 'manager_overview_unit_customers_suffix'),
        withUnit(this.$t('manager_overview_abc_target_customers'), this.selectedMember.abcTargetCustomerCountText, 'manager_overview_unit_customers_suffix'),
        withUnit(this.$t('manager_overview_target_covered_customers'), this.selectedMember.coveredCustomerCount, 'manager_overview_unit_customers_suffix'),
        withUnit(this.$t('manager_overview_abc_covered_customers'), this.selectedMember.abcCoveredCustomerCount, 'manager_overview_unit_customers_suffix'),
        withPercent(this.$t('manager_overview_target_coverage_rate'), this.selectedMember.coverageRateText),
        withPercent(this.$t('manager_overview_abc_coverage_rate'), this.selectedMember.abcCoverageRateText),
        withUnit(this.$t('manager_overview_call_count'), this.selectedMember.callCount, 'manager_overview_unit_calls_suffix'),
        withUnit(this.$t('manager_overview_feedback_count'), this.selectedMember.feedbackCount, 'manager_overview_unit_calls_suffix'),
      ];
      if (!this.hideTaskAndNba) {
        metrics.push(
          withPercent(this.$t('manager_overview_task_execution_rate'), this.selectedMember.taskExecutionRateText),
          withPercent(this.$t('manager_overview_nba_process_rate'), this.selectedMember.nbaRateText),
        );
      }
      metrics.push(
        withUnit(this.$t('manager_overview_content_open_send'), `${this.selectedMember.contentOpenCount}/${this.selectedMember.contentSendCount || '--'}`, 'manager_overview_unit_times_suffix'),
        {
          ...withUnit(
            this.$t('manager_overview_content_open_send_customers'),
            `${this.selectedMember.contentOpenCustomerCount}/${this.selectedMember.contentReceiverIds.size}`,
            'manager_overview_unit_customers_suffix',
          ),
          action: 'content-customer-detail',
        },
        withUnit(this.$t('manager_overview_avg_open_duration'), this.selectedMember.contentAverageOpenDurationText, 'manager_overview_unit_seconds_per_article_suffix'),
      );
      return metrics;
    },
  },
  watch: {
    filteredMemberRows(rows) {
      if (!rows.length) {
        this.selectedMemberId = '';
        this.memberDetailVisible = false;
        return;
      }
      if (!rows.find(item => item.id === this.selectedMemberId)) {
        this.selectedMemberId = rows[0].id;
      }
      if (this.memberDetailVisible) {
        this.loadSelectedMemberPromotionStats();
      }
    },
    selectedMemberId() {
      this.closeMemberContentDetail();
      if (this.memberDetailVisible) {
        this.loadSelectedMemberPromotionStats();
      }
    },
    selectedAreaTerritoryId() {
      if (
        this.selectedDistrictTerritoryId &&
        !this.districtOptions.some(item => item.value === this.selectedDistrictTerritoryId)
      ) {
        this.selectedDistrictTerritoryId = '';
      }
    },
    showAreaMemberFilter(visible) {
      if (!visible && this.selectedAreaTerritoryId) {
        this.selectedAreaTerritoryId = '';
      }
    },
  },
  created() {
    this.fetchRoleOptions();
    if (this.isManagerRole) {
      this.loadData();
    } else {
      this.loading = false;
    }
  },
  methods: {
    selectDistrictOption(option = {}) {
      this.selectedDistrictTerritoryId = option.value || '';
      if (this.$refs.districtDropdown?.toggle) {
        this.$refs.districtDropdown.toggle(false);
      }
    },
    getRoleDisplayName(roleName) {
      if (roleName === '销售' || roleName === 'MR') return this.$t('role_sales');
      if (roleName === '管理员') return this.$t('role_admin');
      if (roleName === '市场部') return this.$t('role_marketing');
      if (roleName === 'GA管理员') return this.$t('role_ga_admin');
      return roleName || '--';
    },
    isSameRoleName(left, right) {
      const normalizeRole = role => (role === '销售' ? 'MR' : role);
      return normalizeRole(left) === normalizeRole(right);
    },
    fetchRoleOptions() {
      try {
        const loginRolesStr = getLocalStorage('login_roles');
        const roles = loginRolesStr ? JSON.parse(loginRolesStr) : [];
        this.roleActions = (roles || [])
          .filter(item => item && item.role_name)
          .sort((a, b) => (Number(a.role_id) > Number(b.role_id) ? 1 : -1))
          .map(item => ({
            name: this.getRoleDisplayName(item.role_name),
            displayName: this.getRoleDisplayName(item.role_name),
            role_name: item.role_name,
            role_id: item.role_id,
            color: this.isSameRoleName(item.role_name, this.currentRole) ? '#aa341b' : undefined,
          }));
      } catch (error) {
        console.error('Fetch roles failed', error);
        this.roleActions = [];
      }
    },
    applySwitchedUser(user = {}) {
      clearManagerOverviewPeriodState();
      const normalizedUser = normalizeSalesOverviewContext(user);
      setLocalStorage('is_sales', user.role_name === '销售' ? '1' : '0');
      setLocalStorage('user_id', user.user_id);
      setLocalStorage('user_name', user.user_name);
      setLocalStorage('territory_id', normalizedUser.territory_id);
      setLocalStorage('territory_type', normalizedUser.territory_type);
      setLocalStorage('role_id', user.role_id);
      setLocalStorage('role_name', user.role_name);
      setLocalStorage('enable_ai', resolveEnableAI(user.enable_ai, user.role_id, user.role_name) ? '1' : '0');

      const medicalRoles = ['MSL', 'MA', '医学经理', '医学部管理员'];
      const isMsl = medicalRoles.includes(user.role_name);
      setLocalStorage('is_msl', isMsl ? '1' : '0');
      return {normalizedUser, isMsl};
    },
    routeByRole(user, isMsl) {
      if (user && user.role_name === 'GA管理员') {
        window.location.href = '/ga-admin/overview';
        return;
      }
      if (isGBA()) {
        window.location.href = '/gba/overview';
        return;
      }
      if (isMsl) {
        window.location.href = '/kol/home';
        return;
      }
      window.location.href = isSalesManagerTerritory() || isEnableAI() ? '/hcp/overview' : '/hcp/list';
    },
    async onSelectRole(action) {
      const selectedRoleName = action.role_name || action.name;
      if (this.isSameRoleName(this.currentRole, selectedRoleName)) {
        this.showRoleSelector = false;
        return;
      }

      Toast.loading({
        message: this.$t('role_switching'),
        forbidClick: true,
      });

      try {
        const res = await switchRole(action.role_id);
        if (!(res && res.isSuccess && res.data)) {
          Toast.fail(res.message || this.$t('role_switch_failed'));
          return;
        }
        const {token, seed, user} = res.data;
        setToken({token, seed});
        if (!user) {
          Toast.fail(this.$t('role_switch_failed'));
          return;
        }
        const {normalizedUser, isMsl} = this.applySwitchedUser(user);
        this.userId = user.user_id || '';
        this.userName = user.user_name || '';
        this.currentRole = user.role_name || selectedRoleName;
        this.roleId = user.role_id || action.role_id || '';
        this.territoryId = normalizedUser.territory_id || '';
        this.territoryType = normalizedUser.territory_type || '';
        this.roleActions.forEach((item) => {
          item.color = this.isSameRoleName(item.role_name, user.role_name) ? '#aa341b' : undefined;
        });
        Toast.success(this.$t('role_switch_success', {role: this.getRoleDisplayName(user.role_name)}));
        this.routeByRole(user, isMsl);
      } catch (error) {
        console.error(error);
        Toast.fail(this.$t('role_switch_failed'));
      }
    },
    getDisplayProductName(item) {
      const name = getProductName(item);
      return name === '未知产品' ? this.$t('manager_overview_unknown_product') : name;
    },
    formatMetricPercent(value) {
      return formatPercent(value);
    },
    async loadSelectedMemberPromotionStats() {
      const member = this.selectedMember;
      const userId = member?.userId || member?.id || '';
      const quarter = this.promotionQuarterKey;
      const cacheKey = this.selectedMemberPromotionCacheKey;
      if (!userId || !quarter || !cacheKey || this.memberPromotionStatsByKey[cacheKey]) return;

      this.memberPromotionLoadingKey = cacheKey;
      if (this.memberPromotionErrorKey === cacheKey) this.memberPromotionErrorKey = '';
      try {
        const rows = await fetchMemberContentInteractionStats({quarter, userId});
        this.$set(this.memberPromotionStatsByKey, cacheKey, summarizeContentInteractionStats(rows));
      } catch (error) {
        console.error('Fetch member content interaction stats failed', error);
        this.memberPromotionErrorKey = cacheKey;
        this.$set(this.memberPromotionStatsByKey, cacheKey, summarizeContentInteractionStats([]));
      } finally {
        if (this.memberPromotionLoadingKey === cacheKey) this.memberPromotionLoadingKey = '';
      }
    },
    getAncestorTerritory(territory, predicate) {
      let current = territory;
      const visited = new Set();
      while (current) {
        const currentId = String(current?.territory_id || '');
        if (currentId) {
          if (visited.has(currentId)) return null;
          visited.add(currentId);
        }
        if (predicate(current)) return current;
        const superiorId = String(current?.superior_territory_id || '');
        if (!superiorId) return null;
        current = this.territoryByIdMap.get(superiorId);
      }
      return null;
    },
    buildProductSegmentScopeKey({territoryIds = [], userIds = []} = {}) {
      const normalize = list => uniq(list).map(item => String(item)).sort().join(',');
      return `users:${normalize(userIds)}|territories:${normalize(territoryIds)}`;
    },
    getPeriodStateContext() {
      return {
        userId: this.userId,
        territoryId: this.territoryId,
        territoryType: this.effectiveTerritoryType,
      };
    },
    saveCurrentPeriodState() {
      savePeriodState(this.getPeriodStateContext(), {
        periodType: this.periodType,
        periodKey: this.periodKey,
      });
    },
    getBaseReportCacheKey() {
      return [
        this.userId || 'unknown-user',
        this.territoryId || 'unknown-territory',
        this.effectiveTerritoryType || 'unknown-type',
        this.periodType,
        this.periodKey,
        this.hideTaskAndNba ? 'without-task-nba' : 'with-task-nba',
      ].join('|');
    },
    createBaseReportCacheSnapshot() {
      return {
        territoryHierarchy: cloneList(this.territoryHierarchy),
        subordinateTerritories: cloneList(this.subordinateTerritories),
        callStats: cloneList(this.callStats),
        feedbackStats: cloneList(this.feedbackStats),
        coverageStats: cloneList(this.coverageStats),
        contentOpenStats: cloneList(this.contentOpenStats),
        contentSendStats: cloneList(this.contentSendStats),
        targetCustomerStats: cloneList(this.targetCustomerStats),
        abcTargetCustomerStats: cloneList(this.abcTargetCustomerStats),
        taskStats: cloneList(this.taskStats),
        nbaSuggestionStats: cloneList(this.nbaSuggestionStats),
        nbaFeedbackStats: cloneList(this.nbaFeedbackStats),
        coveredTargetCustomerStats: cloneList(this.coveredTargetCustomerStats),
        abcCoveredTargetCustomerStats: cloneList(this.abcCoveredTargetCustomerStats),
        coveredTargetProductStats: cloneList(this.coveredTargetProductStats),
        abcCoveredTargetProductStats: cloneList(this.abcCoveredTargetProductStats),
        loadErrors: cloneList(this.loadErrors),
      };
    },
    applyBaseReportCacheSnapshot(snapshot = {}) {
      this.territoryHierarchy = cloneList(snapshot.territoryHierarchy);
      this.subordinateTerritories = cloneList(snapshot.subordinateTerritories);
      this.callStats = cloneList(snapshot.callStats);
      this.feedbackStats = cloneList(snapshot.feedbackStats);
      this.coverageStats = cloneList(snapshot.coverageStats);
      this.contentOpenStats = cloneList(snapshot.contentOpenStats);
      this.contentSendStats = cloneList(snapshot.contentSendStats);
      this.targetCustomerStats = cloneList(snapshot.targetCustomerStats);
      this.abcTargetCustomerStats = cloneList(snapshot.abcTargetCustomerStats);
      this.taskStats = cloneList(snapshot.taskStats);
      this.nbaSuggestionStats = cloneList(snapshot.nbaSuggestionStats);
      this.nbaFeedbackStats = cloneList(snapshot.nbaFeedbackStats);
      this.coveredTargetCustomerStats = cloneList(snapshot.coveredTargetCustomerStats);
      this.abcCoveredTargetCustomerStats = cloneList(snapshot.abcCoveredTargetCustomerStats);
      this.coveredTargetProductStats = cloneList(snapshot.coveredTargetProductStats);
      this.abcCoveredTargetProductStats = cloneList(snapshot.abcCoveredTargetProductStats);
      this.loadErrors = cloneList(snapshot.loadErrors);
    },
    setLoadedProgress() {
      this.resetLoadingProgress({
        totalModules: 1,
        currentLabel: this.$t('manager_overview_loading_finalize'),
      });
      this.updateLoadingProgress({
        completedModules: 1,
        percent: 100,
      });
    },
    afterBaseReportReady() {
      if (this.isTeamReport) {
        this.ensureTeamDetailData();
      }
      if (this.isOverviewReport) {
        this.ensureAnalysisData();
      }
    },
    refreshCoveredTargetCustomerStats() {
      const coveredUserPhysicianKeys = new Set(this.coverageStats.map((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return ownerId && physicianId ? `${ownerId}_${physicianId}` : '';
      }).filter(Boolean));
      this.coveredTargetCustomerStats = this.targetCustomerStats.filter((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return coveredUserPhysicianKeys.has(`${ownerId}_${physicianId}`);
      });
      this.abcCoveredTargetCustomerStats = this.abcTargetCustomerStats.filter((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return coveredUserPhysicianKeys.has(`${ownerId}_${physicianId}`);
      });
    },
    refreshCoveredTargetProductStats() {
      const coveredUserPhysicianKeys = new Set(this.coverageStats.map((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return ownerId && physicianId ? `${ownerId}_${physicianId}` : '';
      }).filter(Boolean));
      this.coveredTargetProductStats = this.targetProductStats.filter((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return coveredUserPhysicianKeys.has(`${ownerId}_${physicianId}`);
      });
      this.abcCoveredTargetProductStats = this.abcTargetProductStats.filter((item) => {
        const ownerId = getStatOwner(item).id;
        const physicianId = getPhysicianId(item);
        return coveredUserPhysicianKeys.has(`${ownerId}_${physicianId}`);
      });
    },
    resetLoadingProgress({totalModules = 1, currentLabel = ''} = {}) {
      this.loadingProgress = {
        completedModules: 0,
        currentLabel,
        expectedMembers: 0,
        fetchedRows: 0,
        percent: 0,
        totalModules,
      };
    },
    updateLoadingProgress(patch = {}) {
      this.loadingProgress = {
        ...this.loadingProgress,
        ...patch,
      };
    },
    completeLoadingModule(requestId, {label, rows = 0, percentCap = 99} = {}) {
      if (requestId !== this.loadRequestId) return;
      const totalModules = Math.max(Number(this.loadingProgress.totalModules) || 1, 1);
      const completedModules = Math.min(this.loadingProgress.completedModules + 1, totalModules);
      const percent = Math.min(percentCap, Math.round((completedModules / totalModules) * 100));
      this.updateLoadingProgress({
        completedModules,
        currentLabel: label || this.loadingProgress.currentLabel,
        fetchedRows: this.loadingProgress.fetchedRows + (Number(rows) || 0),
        percent,
      });
    },
    async runLoadingJob(job, requestId) {
      if (requestId !== this.loadRequestId) return;
      this.updateLoadingProgress({currentLabel: job.label});

      try {
        const value = await job.request();
        if (requestId !== this.loadRequestId) return;
        if (job.onSuccess) job.onSuccess(Array.isArray(value) ? value : []);
        this.completeLoadingModule(requestId, {
          label: job.label,
          rows: Array.isArray(value) ? value.length : 0,
        });
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        console.error(job.errorMessage, error);
        this.loadErrors.push(job.errorMessage);
        if (job.onError) job.onError(error);
        this.completeLoadingModule(requestId, {label: job.label});
      }
    },
    deriveCallStatsFromCoverageStats(list = []) {
      const map = new Map();
      (list || []).forEach((stat) => {
        const owner = getStatOwner(stat);
        if (!owner.id || owner.id === 'unknown') return;
        if (!map.has(owner.id)) {
          map.set(owner.id, {
            call_count: 0,
            call_user_id: owner.id,
            call_user_name: owner.name,
          });
        }
        map.get(owner.id).call_count += this.getStatCount(stat, 'call_count');
      });
      return Array.from(map.values());
    },
    async ensureProductSegmentData({territoryIds, userIds, force = false} = {}) {
      const scopeKey = this.buildProductSegmentScopeKey({territoryIds, userIds});
      const shouldFetchSegments = force || !this.productSegmentLoaded || this.productSegmentScopeKey !== scopeKey;
      const shouldFetchDefinitions = force || !this.physicianTypingDefinitionsLoaded;

      if (!shouldFetchSegments && !shouldFetchDefinitions) return;

      if (shouldFetchSegments && this.productSegmentScopeKey !== scopeKey) {
        this.productSegmentStats = [];
        this.productSegmentLoaded = false;
        this.productSegmentScopeKey = scopeKey;
      }

      const jobs = [];
      if (shouldFetchSegments) {
        jobs.push({
          type: 'segments',
          promise: fetchManagerProductSegmentStats({territoryIds, userIds}),
        });
      }
      if (shouldFetchDefinitions) {
        jobs.push({
          type: 'definitions',
          promise: fetchManagerPhysicianTypingDefinitions(),
        });
      }

      const results = await Promise.allSettled(jobs.map(item => item.promise));
      let segmentsLoaded = !shouldFetchSegments;
      results.forEach((result, index) => {
        const job = jobs[index];
        if (!job) return;
        if (result.status !== 'fulfilled') {
          console.error(`Fetch product segment ${job.type} failed`, result.reason);
          this.loadErrors.push(job.type === 'definitions'
            ? this.$t('manager_overview_typing_definitions_unavailable')
            : this.$t('manager_overview_product_target_unavailable'));
          return;
        }
        if (job.type === 'segments') {
          this.productSegmentStats = result.value || [];
          this.productSegmentScopeKey = scopeKey;
          segmentsLoaded = true;
        }
        if (job.type === 'definitions') {
          this.physicianTypingDefinitions = result.value || [];
          this.physicianTypingDefinitionsLoaded = true;
        }
      });
      this.productSegmentLoaded = segmentsLoaded;
    },
    async ensureAnalysisData() {
      if (!this.subordinateTerritories.length) return;

      const territoryIds = this.subordinateTerritoryIds;
      const userIds = this.subordinateUserIds;
      const scopeKey = this.buildProductSegmentScopeKey({territoryIds, userIds});
      const cachedAnalysis = ANALYSIS_CACHE.get(scopeKey);
      if (cachedAnalysis) {
        this.productSegmentStats = cloneList(cachedAnalysis.productSegmentStats);
        this.productSegmentScopeKey = scopeKey;
        this.productSegmentLoaded = true;
        this.physicianTypingDefinitions = cloneList(cachedAnalysis.physicianTypingDefinitions);
        this.physicianTypingDefinitionsLoaded = true;
        return;
      }

      if (
        this.productSegmentLoaded &&
        this.productSegmentScopeKey === scopeKey &&
        this.physicianTypingDefinitionsLoaded
      ) {
        return;
      }

      this.analysisLoading = true;
      try {
        await this.ensureProductSegmentData({territoryIds, userIds});
        if (this.productSegmentLoaded && this.physicianTypingDefinitionsLoaded) {
          ANALYSIS_CACHE.set(scopeKey, {
            productSegmentStats: cloneList(this.productSegmentStats),
            physicianTypingDefinitions: cloneList(this.physicianTypingDefinitions),
          });
        }
      } finally {
        this.analysisLoading = false;
      }
    },
    async ensureTeamDetailData() {
      if (!this.subordinateTerritories.length) return;

      const territoryIds = this.subordinateTerritoryIds;
      const userIds = this.subordinateUserIds;
      const scopeKey = this.buildProductSegmentScopeKey({territoryIds, userIds});
      const cachedDetail = TEAM_DETAIL_CACHE.get(scopeKey);
      if (cachedDetail) {
        this.targetProductStats = cloneList(cachedDetail.targetProductStats);
        this.abcTargetProductStats = cloneList(cachedDetail.abcTargetProductStats);
        this.targetProductScopeKey = scopeKey;
        this.teamDetailLoaded = true;
        this.refreshCoveredTargetProductStats();
        return;
      }

      if (this.teamDetailLoaded && this.targetProductScopeKey === scopeKey) {
        this.refreshCoveredTargetProductStats();
        return;
      }

      const requestId = this.teamDetailRequestId + 1;
      this.teamDetailRequestId = requestId;
      this.teamDetailLoading = true;
      try {
        const [targetProducts, abcTargetProducts] = await Promise.all([
          fetchManagerTargetProductStats({territoryIds, userIds}),
          fetchManagerTargetProductStats({territoryIds, userIds, abcOnly: true}),
        ]);
        if (requestId !== this.teamDetailRequestId) return;
        this.targetProductStats = targetProducts || [];
        this.abcTargetProductStats = abcTargetProducts || [];
        this.targetProductScopeKey = scopeKey;
        this.teamDetailLoaded = true;
        TEAM_DETAIL_CACHE.set(scopeKey, {
          targetProductStats: cloneList(this.targetProductStats),
          abcTargetProductStats: cloneList(this.abcTargetProductStats),
        });
        this.refreshCoveredTargetProductStats();
      } catch (error) {
        if (requestId !== this.teamDetailRequestId) return;
        console.error('Fetch team detail target products failed', error);
        this.loadErrors.push(this.$t('manager_overview_team_detail_unavailable'));
      } finally {
        if (requestId === this.teamDetailRequestId) {
          this.teamDetailLoading = false;
        }
      }
    },
    async loadData() {
      const requestId = this.loadRequestId + 1;
      this.loadRequestId = requestId;
      this.loading = true;
      this.loadErrors = [];
      this.detailRecentCallError = '';
      this.detailRecentCalls = [];
      this.detailRecentCallsLoading = false;
      this.monthRange = getPeriodRange(this.periodType, this.periodKey);
      this.saveCurrentPeriodState();
      const baseCacheKey = this.getBaseReportCacheKey();
      const cachedBaseReport = BASE_REPORT_CACHE.get(baseCacheKey);
      if (cachedBaseReport) {
        this.applyBaseReportCacheSnapshot(cachedBaseReport);
        this.setLoadedProgress();
        this.loading = false;
        this.afterBaseReportReady();
        return;
      }
      this.resetLoadingProgress({
        totalModules: 2,
        currentLabel: this.$t('manager_overview_loading_hierarchy'),
      });

      try {
        const hierarchy = await fetchManagerSubordinateTerritoryHierarchy({
          territoryId: this.territoryId,
          territoryType: this.effectiveTerritoryType,
        });
        this.territoryHierarchy = hierarchy.descendants || [];
        this.subordinateTerritories = hierarchy.leafSales?.length
          ? hierarchy.leafSales
          : this.territoryHierarchy.filter(item => item?.user_id);
      } catch (e) {
        console.error('Fetch subordinate territories failed', e);
        this.territoryHierarchy = [];
        this.subordinateTerritories = [];
        this.loadErrors.push(this.$t('manager_overview_subordinate_unavailable'));
      }
      if (requestId !== this.loadRequestId) return;
      this.completeLoadingModule(requestId, {
        label: this.$t('manager_overview_loading_hierarchy'),
        rows: this.subordinateTerritories.length,
      });

      if (!this.subordinateTerritories.length) {
        this.loadErrors.push(this.$t('manager_overview_no_subordinates'));
        this.callStats = [];
        this.feedbackStats = [];
        this.coverageStats = [];
        this.contentOpenStats = [];
        this.contentSendStats = [];
        this.targetCustomerStats = [];
        this.abcTargetCustomerStats = [];
        this.taskStats = [];
        this.nbaSuggestionStats = [];
        this.nbaFeedbackStats = [];
        this.coveredTargetCustomerStats = [];
        this.abcCoveredTargetCustomerStats = [];
        this.coveredTargetProductStats = [];
        this.abcCoveredTargetProductStats = [];
        this.completeLoadingModule(requestId, {
          label: this.$t('manager_overview_loading_finalize'),
          percentCap: 100,
        });
        this.updateLoadingProgress({percent: 100});
        BASE_REPORT_CACHE.set(baseCacheKey, this.createBaseReportCacheSnapshot());
        this.loading = false;
        return;
      }

      const territoryIds = this.subordinateTerritoryIds;
      const userIds = this.subordinateUserIds;
      const scopeKey = this.buildProductSegmentScopeKey({territoryIds, userIds});

      this.callStats = [];
      this.feedbackStats = [];
      this.coverageStats = [];
      this.contentOpenStats = [];
      this.contentSendStats = [];
      this.targetCustomerStats = [];
      this.abcTargetCustomerStats = [];
      this.taskStats = [];
      this.nbaSuggestionStats = [];
      this.nbaFeedbackStats = [];
      this.coveredTargetCustomerStats = [];
      this.abcCoveredTargetCustomerStats = [];
      this.coveredTargetProductStats = [];
      this.abcCoveredTargetProductStats = [];

      if (this.productSegmentScopeKey && this.productSegmentScopeKey !== scopeKey) {
        this.productSegmentStats = [];
        this.productSegmentLoaded = false;
      }

      if (this.targetProductScopeKey && this.targetProductScopeKey !== scopeKey) {
        this.targetProductStats = [];
        this.abcTargetProductStats = [];
        this.abcCoveredTargetProductStats = [];
        this.targetProductScopeKey = '';
        this.teamDetailLoaded = false;
      }

      const jobs = [
        {
          label: this.$t('manager_overview_loading_feedback'),
          request: () => fetchManagerFeedbackCallStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            territoryIds,
            userIds,
          }),
          onSuccess: value => { this.feedbackStats = value; },
          errorMessage: this.$t('manager_overview_feedback_stats_unavailable'),
        },
        {
          label: this.$t('manager_overview_loading_coverage'),
          request: () => fetchManagerCoveredCustomerStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            territoryIds,
            userIds,
          }),
          onSuccess: (value) => {
            this.coverageStats = value;
            this.callStats = this.deriveCallStatsFromCoverageStats(value);
          },
          errorMessage: this.$t('manager_overview_coverage_stats_unavailable'),
        },
        {
          label: this.$t('manager_overview_loading_content'),
          request: () => fetchManagerContentSendStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            territoryIds,
            userIds,
          }),
          onSuccess: value => { this.contentSendStats = value; },
          errorMessage: this.$t('manager_overview_content_stats_unavailable'),
        },
        {
          label: this.$t('manager_overview_loading_target_customers'),
          request: () => fetchManagerTargetCustomerStats({territoryIds, userIds}),
          onSuccess: value => { this.targetCustomerStats = value; },
          errorMessage: this.$t('manager_overview_product_target_unavailable'),
        },
        {
          label: this.$t('manager_overview_loading_abc_target_customers'),
          request: () => fetchManagerTargetCustomerStats({
            territoryIds,
            userIds,
            abcOnly: true,
            includeSegment: true,
          }),
          onSuccess: value => { this.abcTargetCustomerStats = value; },
          errorMessage: this.$t('manager_overview_product_target_unavailable'),
        },
      ];

      if (!this.hideTaskAndNba) {
        jobs.push({
          label: this.$t('manager_overview_loading_tasks'),
          request: () => fetchManagerCampaignEventTaskStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            userIds,
          }),
          onSuccess: value => { this.taskStats = this.filterItemsBySubordinates(value); },
          errorMessage: this.$t('manager_overview_task_stats_unavailable'),
        });
        jobs.push({
          label: this.$t('manager_overview_loading_nba'),
          request: () => fetchManagerNbaSuggestionStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            userIds,
          }),
          onSuccess: value => { this.nbaSuggestionStats = value; },
          errorMessage: this.$t('manager_overview_nba_suggestion_stats_unavailable'),
        });
        jobs.push({
          label: this.$t('manager_overview_loading_nba_feedback'),
          request: () => fetchManagerNbaSuggestionFeedbackStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            territoryIds,
            userIds,
          }),
          onSuccess: value => { this.nbaFeedbackStats = value; },
          errorMessage: this.$t('manager_overview_nba_feedback_stats_unavailable'),
        });
      }

      const totalModules = 1 + jobs.length + 1;
      this.updateLoadingProgress({
        currentLabel: this.$t('manager_overview_loading_parallel'),
        expectedMembers: this.subordinateTerritories.length,
        percent: Math.round((this.loadingProgress.completedModules / totalModules) * 100),
        totalModules,
      });
      await Promise.all(jobs.map(job => this.runLoadingJob(job, requestId)));
      if (requestId !== this.loadRequestId) return;

      if (this.contentSendStats.length) {
        try {
          this.contentOpenStats = await fetchManagerContentOpenStats({
            startDate: this.monthRange.startIso,
            endDate: this.monthRange.endIso,
            contentSends: this.contentSendStats,
            targetPhysicianIds: [],
          });
        } catch (error) {
          if (requestId !== this.loadRequestId) return;
          console.error('Fetch content open stats failed', error);
          this.contentOpenStats = [];
          this.loadErrors.push(this.$t('manager_overview_content_open_stats_unavailable'));
        }
      }

      if (
        this.subordinateTerritories.length &&
        !this.callStats.length &&
        !this.feedbackStats.length &&
        !this.coverageStats.length &&
        !this.targetCustomerStats.length &&
        !this.abcTargetCustomerStats.length &&
        !this.contentSendStats.length &&
        !this.contentOpenStats.length &&
        !this.taskStats.length &&
        !this.nbaSuggestionStats.length &&
        !this.nbaFeedbackStats.length
      ) {
        this.loadErrors.push(this.$t('manager_overview_no_period_data', {count: this.subordinateTerritories.length}));
      }

      this.refreshCoveredTargetCustomerStats();
      this.refreshCoveredTargetProductStats();
      BASE_REPORT_CACHE.set(baseCacheKey, this.createBaseReportCacheSnapshot());

      this.completeLoadingModule(requestId, {
        label: this.$t('manager_overview_loading_finalize'),
        percentCap: 100,
      });
      this.updateLoadingProgress({
        currentLabel: this.$t('manager_overview_loading_finalize'),
        percent: 100,
      });
      this.loading = false;
      this.afterBaseReportReady();
    },
    handlePeriodTypeChange() {
      const options = buildPeriodOptions(this.periodType);
      this.periodKey = options[0] ? options[0].value : '';
      this.saveCurrentPeriodState();
      this.loadData();
    },
    handlePeriodKeyChange() {
      this.saveCurrentPeriodState();
      this.loadData();
    },
    openTeamAiAnalysis(scope) {
      this.$root.$emit('open-ai-chat', this.buildTeamAiPrompt(scope), true, SALES_MANAGER_COPILOT_AGENT);
    },
    buildTeamAiPrompt(scope) {
      const isEnglish = String(this.$i18n?.locale || '').toUpperCase().startsWith('EN');
      const separator = isEnglish ? '; ' : '；';
      const abcCoverageText = this.abcCoverageRows
        .map(item => `${item.level} ${item.coveredCustomerCount}/${item.targetCustomerCount || '--'} (${item.coverageRateText})`)
        .join(isEnglish ? ', ' : '，');
      const scopeTitleMap = isEnglish
        ? {
          overall: 'Team Execution Overview',
          'product-segment': 'Customer Perception Segment Overview',
          quality: 'Team Execution Quality',
        }
        : {
          overall: '团队执行总览',
          'product-segment': '客户观念分型总览',
          quality: '团队执行质量',
        };
      const summaryText = this.summaryMetrics
        .map(item => `${item.label}: ${item.value}${item.desc ? ` (${item.desc})` : ''}`)
        .join(separator);
      const qualityText = this.qualityMetrics
        .map(item => `${item.label}: ${item.value}, ${item.desc}`)
        .join(separator);
      const productText = this.productSegmentRows
        .slice(0, 5)
        .map((product) => {
          const segmentRows = product.segments
            .map(item => `${item.name} ${item.targetCustomerCount} (${item.shareText})`)
            .join(isEnglish ? ', ' : '，');
          return isEnglish
            ? `${product.name}: ${product.targetCustomerCount} target customers${segmentRows ? `, segment distribution: ${segmentRows}` : ''}`
            : `${product.name}：目标客户${product.targetCustomerCount}${segmentRows ? `，分型分布：${segmentRows}` : ''}`;
        })
        .join(separator) || (isEnglish ? 'No product data' : '暂无产品数据');
      const memberText = this.filteredMemberRows
        .slice(0, 8)
        .map((member) => {
          const memberAbcCoverageText = member.abcCoverageRows
            .map(item => `${item.level} ${item.coveredCustomerCount}/${item.targetCustomerCount || '--'} (${item.coverageRateText})`)
            .join(isEnglish ? ', ' : '，');
          return isEnglish
            ? `${member.name}: target coverage ${member.coveredCustomerCount}/${member.targetCustomerCountText} (${member.coverageRateText}), A/B/C coverage ${member.abcCoveredCustomerCount}/${member.abcTargetCustomerCountText} (${member.abcCoverageRateText}); by level: ${memberAbcCoverageText}, calls ${member.callCount}, feedback ${member.feedbackCount}, content opens/sends ${member.contentOpenCount}/${member.contentSendCount || '--'}, reached customers ${member.contentReceiverIds.size}, avg read ${member.contentAverageOpenDurationText}/article${this.hideTaskAndNba ? '' : `, tasks ${member.taskExecuted}/${member.taskTotalText}, NBA executed ${member.nbaExecuted}`}`
            : `${member.name}：目标客户覆盖${member.coveredCustomerCount}/${member.targetCustomerCountText}（${member.coverageRateText}），A/B/C覆盖${member.abcCoveredCustomerCount}/${member.abcTargetCustomerCountText}（${member.abcCoverageRateText}）；分级覆盖：${memberAbcCoverageText}，拜访${member.callCount}，反馈${member.feedbackCount}，文章打开/发送${member.contentOpenCount}/${member.contentSendCount || '--'}，触达客户${member.contentReceiverIds.size}，平均阅读${member.contentAverageOpenDurationText}/篇${this.hideTaskAndNba ? '' : `，任务${member.taskExecuted}/${member.taskTotalText}，NBA执行${member.nbaExecuted}`}`;
        })
        .join(separator) || (isEnglish ? 'No member data' : '暂无成员数据');
      const noDataText = isEnglish ? 'No data' : '暂无数据';
      const commonContext = isEnglish
        ? [
          `Current manager role: ${this.roleLabel}; current user: ${this.userName || this.userId}; execution period: ${this.periodRangeText}; product and segment scope: current latest; subordinate count: ${this.memberRows.length}; currently filtered members: ${this.filteredMemberRows.length}.`,
          `Team execution overview: ${summaryText || noDataText}.`,
          `Team execution quality: ${qualityText || noDataText}.`,
          `Target customer coverage: ${this.coveredCustomerCount}/${this.targetCustomerCount || '--'}, coverage rate ${this.coverageRateText}; A/B/C customer coverage: ${this.abcCoveredCustomerCount}/${this.abcTargetCustomerCount || '--'}, coverage rate ${this.abcCoverageRateText}; by level: ${abcCoverageText}.`,
          `Content opens/sends by target physicians: ${this.contentTargetOpenCount}/${this.contentSendCount || '--'}; reached customers: ${this.contentReachedCustomerCount}; average read duration: ${this.contentAverageOpenDurationText}/article.`,
          `Customer perception segments: ${productText}.`,
          `Member performance summary: ${memberText}.`,
        ].join('\n')
        : [
          `当前管理角色：${this.roleLabel}；当前用户：${this.userName || this.userId}；执行数据周期：${this.periodRangeText}；产品与分型口径：当前最新；下属人数：${this.memberRows.length}；当前成员筛选显示：${this.filteredMemberRows.length}人。`,
          `团队执行总览：${summaryText || noDataText}。`,
          `团队执行质量：${qualityText || noDataText}。`,
          `目标客户覆盖：${this.coveredCustomerCount}/${this.targetCustomerCount || '--'}，覆盖率${this.coverageRateText}；A/B/C客户覆盖：${this.abcCoveredCustomerCount}/${this.abcTargetCustomerCount || '--'}，覆盖率${this.abcCoverageRateText}；分级覆盖：${abcCoverageText}。`,
          `文章打开/发送：${this.contentTargetOpenCount}/${this.contentSendCount || '--'}；触达客户：${this.contentReachedCustomerCount}；平均阅读：${this.contentAverageOpenDurationText}/篇。`,
          `客户观念分型：${productText}。`,
          `成员表现摘要：${memberText}。`,
        ].join('\n');
      const promptMap = isEnglish
        ? {
          overall: [
            'Act as a sales management insight assistant and focus on the "Team Execution Overview" section.',
            commonContext,
            'Assess the team execution status for this period: whether coverage is sufficient, whether calls and feedback are aligned, and whether content push, task execution, or NBA execution show clear gaps.',
            'Use this structure: 1) Overall assessment; 2) Key execution strengths; 3) Main gaps and risks; 4) Priority management actions for this period; 5) Follow-up questions or additional data needed.',
            'Avoid generic summaries. Recommendations should be actionable for territory management or team cadence adjustment. Do not invent data that is not in the system.',
          ],
          quality: [
            'Act as a sales management insight assistant and focus on the "Team Execution Quality" section.',
            commonContext,
            'Focus on feedback completion, target customer coverage, A/B/C customer coverage, content reach, task execution, and NBA handling to identify weak points most likely to affect management quality.',
            'Use this structure: 1) Quality metric diagnosis; 2) Abnormal or inefficient steps; 3) Possible management reasons; 4) Coaching focus; 5) Quality improvement actions for the next period.',
            'Separate facts from analytical judgment, and do not make inappropriate personal evaluations based only on volume.',
          ],
          'product-segment': [
            'Act as a sales management insight assistant and focus on the "Customer Perception Segment Overview" section.',
            commonContext,
            'Analyze the current physician perception segment distribution of all target customers by product, and assess product-level maturity differences and potential advancement bottlenecks.',
            'Use this structure: 1) Product-level perception distribution assessment; 2) Main opportunity groups or stages; 3) Priority perception stages to advance; 4) Academic communication or content support suggestions; 5) Data limitations.',
            'Segment data uses the current latest scope, not changes within the selected period. Do not interpret it as a time trend.',
          ],
        }
        : {
          overall: [
            '请作为销售管理团队洞察助手，重点分析「团队执行总览」板块。',
            commonContext,
            '请围绕本周期团队总体执行态势进行判断：覆盖是否充分、拜访与反馈是否匹配、文章推送/任务/NBA执行是否有明显短板。',
            '输出请使用以下结构：1）总体判断；2）关键执行亮点；3）主要短板和风险；4）建议本周期优先管理动作；5）需要进一步追问或补充的数据。',
            '请避免泛泛总结，建议要能落到区域管理动作或团队节奏调整上；不得编造系统中没有的数据。',
          ],
          quality: [
            '请作为销售管理团队洞察助手，重点分析「团队执行质量」板块。',
            commonContext,
            '请聚焦反馈完成、目标客户覆盖、A/B/C客户覆盖、文章触达、任务执行和NBA处理这些质量指标，识别执行链路中最可能影响管理质量的薄弱环节。',
            '输出请使用以下结构：1）质量指标诊断；2）异常或低效环节；3）可能的管理原因；4）coaching重点；5）下周期质量提升动作。',
            '请区分事实数据和分析判断，不能仅根据数量高低对个人作不当评价。',
          ],
          'product-segment': [
            '请作为销售管理团队洞察助手，重点分析「客户观念分型总览」板块。',
            commonContext,
            '请按产品分析全部目标客户当前医生观念分型分布，判断不同产品的客户观念推进阶段、成熟度差异和潜在推进瓶颈。',
            '输出请使用以下结构：1）分产品观念分布判断；2）主要机会人群或阶段；3）需要优先推进的观念阶段；4）建议的学术沟通/内容支持方向；5）数据限制。',
            '请注意分型数据是当前最新口径，不是所选周期内新增变化；不要把它解读成时间趋势。',
          ],
        };

      return (promptMap[scope] || [
        isEnglish
          ? `Please analyze the "${scopeTitleMap[scope] || 'Team Execution'}" section based on the current sales management team report and provide management suggestions.`
          : `请基于当前销售管理团队报告，进一步分析「${scopeTitleMap[scope] || '团队执行'}」板块，并给出管理建议。`,
        commonContext,
        isEnglish
          ? 'Separate facts, analytical judgments, and action recommendations. Do not invent data that is not in the system.'
          : '请区分事实、分析判断和行动建议，不要编造系统中没有的数据。',
      ]).join('\n');
    },
    openMemberDetail(memberId) {
      this.teamScrollTop = this.getPageScrollTop();
      this.selectedMemberId = memberId;
      this.memberDetailVisible = true;
      this.loadSelectedMemberPromotionStats();
      this.loadMemberRecentCalls();
    },
    handleDetailMetricClick(item) {
      if (!item || item.action !== 'content-customer-detail') return;
      this.openMemberContentDetail();
    },
    async openMemberContentDetail() {
      const member = this.selectedMember;
      if (!member) return;

      const requestId = this.memberContentDetailRequestId + 1;
      const memberUserId = String(member.userId || member.id || '');
      const contentSends = this.contentSendStats.filter((item) => getStatOwner(item).id === memberUserId);

      this.memberContentDetailRequestId = requestId;
      this.memberContentDetailVisible = true;
      this.memberContentDetailLoading = true;
      this.memberContentDetailError = '';
      this.memberContentDetailFilter = 'all';
      this.memberContentDetailRows = [];

      if (!contentSends.length) {
        this.memberContentDetailLoading = false;
        return;
      }

      try {
        const contentOpens = this.contentOpenStats.filter((item) => getStatOwner(item).id === memberUserId);

        const customerMap = new Map();
        contentSends.forEach((item) => {
          const physicianId = getPhysicianId(item);
          if (!physicianId) return;
          if (!customerMap.has(physicianId)) {
            customerMap.set(physicianId, {physicianId, sendCount: 0, openCount: 0});
          }
          customerMap.get(physicianId).sendCount += this.getStatCount(item, 'send_count') || 0;
        });
        contentOpens.forEach((item) => {
          const physicianId = getPhysicianId(item);
          if (!physicianId || !customerMap.has(physicianId)) return;
          customerMap.get(physicianId).openCount += this.getStatCount(item, 'open_count') || 0;
        });

        const physicianIds = Array.from(customerMap.keys());
        const physicians = await fetchManagerPhysiciansByIds(physicianIds);
        if (requestId !== this.memberContentDetailRequestId) return;
        const physicianMap = new Map(physicians.map(item => [getPhysicianId(item), item]));

        this.memberContentDetailRows = Array.from(customerMap.values())
          .map((item) => {
            const physician = physicianMap.get(item.physicianId) || {};
            return {
              ...item,
              physicianName: getPhysicianName({...physician, physician_id: item.physicianId}),
              institutionName: physician.institution_name || '',
              opened: item.openCount > 0,
            };
          })
          .sort((a, b) => Number(b.opened) - Number(a.opened) || a.physicianName.localeCompare(b.physicianName, 'zh-CN'));
      } catch (error) {
        if (requestId !== this.memberContentDetailRequestId) return;
        console.error('Fetch member content customer detail failed', error);
        this.memberContentDetailError = this.$t('manager_content_load_error');
      } finally {
        if (requestId === this.memberContentDetailRequestId) {
          this.memberContentDetailLoading = false;
        }
      }
    },
    closeMemberContentDetail() {
      this.memberContentDetailVisible = false;
      this.memberContentDetailRequestId += 1;
      this.memberContentDetailLoading = false;
    },
    backToMemberList() {
      this.closeMemberContentDetail();
      this.memberDetailVisible = false;
      this.detailRecentRequestId += 1;
      this.detailRecentCallsLoading = false;
      this.$nextTick(() => {
        this.restorePageScroll();
      });
    },
    getPageScrollTop() {
      return window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
    },
    restorePageScroll() {
      window.scrollTo(0, this.teamScrollTop || 0);
    },
    async loadMemberRecentCalls() {
      const requestId = this.detailRecentRequestId + 1;
      this.detailRecentRequestId = requestId;
      const member = this.selectedMember;
      if (!member) {
        this.detailRecentCalls = [];
        return;
      }

      const userIds = member.id && member.id !== 'unknown' ? [member.id] : [];
      const territoryIds = member.territoryId ? [member.territoryId] : [];
      if (!userIds.length && !territoryIds.length) {
        this.detailRecentCalls = [];
        return;
      }

      this.detailRecentCallsLoading = true;
      this.detailRecentCallError = '';
      this.detailRecentCalls = [];

      try {
        const calls = await fetchManagerRecentCalls({
          startDate: this.monthRange.startIso,
          endDate: this.monthRange.endIso,
          territoryIds,
          userIds,
          top: 20,
        });
        if (requestId !== this.detailRecentRequestId) return;
        const physicianIds = uniq(calls.map(getPhysicianId));
        let physicianMap = new Map();
        if (physicianIds.length) {
          try {
            const physicians = await fetchManagerPhysiciansByIds(physicianIds);
            if (requestId !== this.detailRecentRequestId) return;
            physicianMap = new Map(physicians.map(item => [getPhysicianId(item), item]));
          } catch (e) {
            console.error('Fetch recent call physicians failed', e);
          }
        }
        this.detailRecentCalls = calls.map((call, index) => ({
          id: getCallId(call) || `${member.id}-${index}`,
          physicianName: getPhysicianName(physicianMap.get(getPhysicianId(call)) || call),
          institutionName: physicianMap.get(getPhysicianId(call))?.institution_name || call?.institution_name || '',
          date: formatDate(call.call_time || call.call_date),
        }));
      } catch (e) {
        if (requestId !== this.detailRecentRequestId) return;
        console.error('Fetch member recent calls failed', e);
        this.detailRecentCallError = this.$t('manager_overview_recent_calls_unavailable');
      } finally {
        if (requestId === this.detailRecentRequestId) {
          this.detailRecentCallsLoading = false;
        }
      }
    },
    getStatCount(item, key) {
      if (!item) return 0;
      const candidates = [
        item[key],
        item[String(key).toLowerCase()],
        item[String(key).toUpperCase()],
        item.count,
        item.Count,
        item.$count,
        item['@odata.count'],
        item.data_count,
      ];
      const value = candidates.find(candidate => candidate !== undefined && candidate !== null && candidate !== '');
      return Number(value || 0) || 0;
    },
    sumStats(list, key) {
      return (list || []).reduce((total, item) => total + this.getStatCount(item, key), 0);
    },
    filterItemsBySubordinates(list = []) {
      const userIdSet = new Set(this.subordinateUserIds);
      const userNameSet = new Set(this.subordinateUserNames);
      const territoryIdSet = new Set(this.subordinateTerritoryIds);
      if (!userIdSet.size && !territoryIdSet.size && !userNameSet.size) return [];

      return (list || []).filter((item) => {
        const owner = getTaskOwner(item);
        const ownerId = owner.id;
        const ownerName = owner.name;
        const territoryId = getTaskTerritoryId(item);
        return (
          (ownerId && ownerId !== 'unknown' && userIdSet.has(ownerId)) ||
          (ownerName && ownerName !== '未知成员' && userNameSet.has(ownerName)) ||
          (territoryId && territoryIdSet.has(territoryId))
        );
      });
    },
    getSettledValue(result, message) {
      if (result.status === 'fulfilled') {
        return Array.isArray(result.value) ? result.value : [];
      }
      console.error(message, result.reason);
      this.loadErrors.push(message);
      return [];
    },
  },
};
</script>

<style lang="scss" scoped>
.team-page {
  min-height: 100vh;
  padding: 0 12px 90px;
  overflow-x: hidden;
  background-color: #f2f5f9;
  background: linear-gradient(175deg, #e8f1ff 0%, #f4edf5 32%, #f2f5f9 58%);
  color: #323233;
  text-align: left;
}

.page-card,
.section-card {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.team-header {
  position: relative;
  z-index: 10;
  box-sizing: border-box;
  min-height: 170px;
  margin: 0 -12px 12px;
  padding: 22px 20px 20px;
  overflow: visible;
  color: #fff;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0) 42%),
    linear-gradient(165deg, #4d50e9 8%, #b84fd7 34%, #aa341b 78%);
  border-bottom-left-radius: 22px;
  border-bottom-right-radius: 22px;
  box-shadow: 0 14px 28px rgba(80, 59, 135, 0.2);
}

.team-header::after {
  content: '';
  position: absolute;
  inset: auto 0 -20px;
  height: 50px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0));
  transform: rotate(-3deg);
  pointer-events: none;
}

.team-header-content,
.header-stats {
  position: relative;
  z-index: 2;
}

.team-header-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.team-title-row {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 8px;
}

.team-title-row > div {
  min-width: 0;
}

.team-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #333;
  background: #ecf0fd;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.team-role-switcher {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  max-width: 116px;
  min-height: 30px;
  padding: 5px 10px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.team-role-switcher .role-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-period-filter {
  position: relative;
  z-index: 40;
  display: grid;
  flex: 1 0 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding-top: 4px;
}

.header-period-filter ::v-deep .van-dropdown-menu__bar {
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: none;
}

.header-period-filter ::v-deep .van-dropdown-menu__title {
  max-width: 100%;
  padding: 0 18px 0 10px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.header-period-filter ::v-deep .van-dropdown-menu__title::after {
  right: 4px;
  border-color: transparent transparent #fff #fff;
}

.header-period-filter ::v-deep .van-dropdown-item {
  z-index: 3000;
}

.header-period-filter ::v-deep .van-dropdown-item__content {
  z-index: 3001;
}

.header-stats {
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
  min-height: 56px;
  padding: 12px 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.14);
}

.header-stats div {
  min-width: 0;
}

.header-stats > div {
  flex: 0 1 auto;
}

.header-stats > div:first-child {
  flex-shrink: 0;
  min-width: max-content;
}

.header-stat-right {
  text-align: right;
}

.header-stats > div > strong,
.header-stats > div > span {
  display: block;
}

.header-stats > div > strong {
  max-width: 100%;
  overflow: hidden;
  font-size: 18px;
  line-height: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  white-space: nowrap;
}

.header-stats > div > span {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 11px;
  line-height: 16px;
}

.coverage-label {
  display: inline-flex;
  align-items: baseline;
  max-width: 100%;
  min-width: 0;
  gap: 3px;
  white-space: nowrap;
}

.coverage-label > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coverage-dot {
  flex: 0 0 auto;
}

.coverage-label b {
  flex: 0 0 auto;
  font-weight: 700;
}

.header-coverage-label b {
  color: #fff;
}

.team-content {
  position: relative;
  z-index: 1;
  margin-top: 0;
}

.page-title-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.page-title {
  font-size: 19px;
  font-weight: 700;
  line-height: 25px;
  color: #fff;
}

.page-scope-tag {
  max-width: 170px;
  padding: 2px 7px;
  border: 1px solid rgba(255, 255, 255, 0.36);
  border-radius: 7px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  line-height: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-subtitle {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 12px;
  line-height: 16px;
  white-space: normal;
}

.state-block {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-progress-card {
  width: min(260px, 76vw);
}

.loading-progress-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.loading-progress-track {
  height: 5px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #f0e2dd;
}

.loading-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #aa341b 0%, #d77a48 100%);
  transition: width 0.25s ease;
}

.loading-progress-step {
  min-width: 0;
  color: #596579;
  font-size: 11px;
  line-height: 16px;
  text-align: left;
}

.loading-progress-step span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading-progress-step small {
  display: block;
  margin-top: 1px;
  color: #8b95a5;
  font-size: 10px;
  line-height: 14px;
}

.loading-progress-percent {
  flex-shrink: 0;
  color: #aa341b;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
}

.warning-box {
  margin-bottom: 12px;
  padding: 12px 14px;
  border: 1px solid #f0d4c9;
  border-radius: 12px;
  color: #8a3b22;
  background: #fff7f4;
  font-size: 12px;
  line-height: 1.5;
}

.section-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #969799;
  font-size: 12px;
}

.analysis-summary {
  margin-bottom: 12px;
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-period {
  flex-shrink: 0;
  margin-top: -1px;
  color: #999;
  font-size: 11px;
  line-height: 20px;
}

.section-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 150px;
  padding-top: 0;
}

.section-actions-vertical {
  flex-direction: column;
  align-items: flex-end;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
  min-height: 26px;
  padding: 5px 9px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #4d50e9 0%, #b84fd7 48%, #aa341b 100%);
  box-shadow: 0 5px 12px rgba(170, 52, 27, 0.18);
  font-size: 11px;
  line-height: 14px;
}

.ai-action-btn span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-desc {
  margin-top: 4px;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

.abc-coverage-total {
  flex: 0 0 auto;
  text-align: right;
}

.abc-coverage-total strong,
.abc-coverage-total span {
  display: block;
}

.abc-coverage-total strong {
  color: #aa341b;
  font-size: 18px;
  line-height: 22px;
  font-variant-numeric: tabular-nums;
}

.abc-coverage-total span {
  margin-top: 3px;
  color: #969799;
  font-size: 10px;
  line-height: 14px;
}

.abc-coverage-grid,
.detail-abc-coverage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.abc-coverage-card,
.detail-abc-coverage-item {
  min-width: 0;
  padding: 11px 8px;
  border: 1px solid #edf1f6;
  border-radius: 12px;
  background: #f8fafc;
  text-align: center;
}

.abc-coverage-card {
  border-top-width: 3px;
}

.abc-coverage-card.level-a {
  border-top-color: #aa341b;
}

.abc-coverage-card.level-b {
  border-top-color: #4d50e9;
}

.abc-coverage-card.level-c {
  border-top-color: #17a772;
}

.abc-coverage-card span,
.abc-coverage-card strong,
.abc-coverage-card small,
.detail-abc-coverage-item span,
.detail-abc-coverage-item strong,
.detail-abc-coverage-item small {
  display: block;
}

.abc-coverage-card .abc-level,
.detail-abc-coverage-item span {
  color: #646566;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.abc-coverage-card strong,
.detail-abc-coverage-item strong {
  margin-top: 4px;
  color: #323233;
  font-size: 17px;
  line-height: 22px;
  font-variant-numeric: tabular-nums;
}

.abc-coverage-card small,
.detail-abc-coverage-item small {
  margin-top: 2px;
  overflow: hidden;
  color: #969799;
  font-size: 10px;
  line-height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.metric-card {
  min-width: 0;
  min-height: 56px;
  overflow: hidden;
  padding: 10px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #fff, #f8f6f5);
  border: 1px solid rgba(226, 232, 240, 0.72);
}

.metric-label {
  min-width: 0;
  color: #666;
  font-size: 12px;
}

.metric-value {
  margin-top: 5px;
  color: #333;
  max-width: 100%;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}

.metric-desc {
  margin-top: 4px;
  color: #999;
  font-size: 11px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.quality-list {
  display: grid;
  gap: 10px;
}

.quality-row {
  padding: 12px 14px;
  border: 1px solid #f2f5f9;
  border-radius: 14px;
  background: #fff;
}

.quality-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #333;
  font-size: 13px;
  font-weight: 600;
}

.quality-head strong {
  color: #aa341b;
}

.progress-track {
  height: 6px;
  margin: 8px 0 6px;
  border-radius: 999px;
  background: #edf2f7;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: #aa341b;
}

.quality-desc {
  color: #999;
  font-size: 12px;
  line-height: 1.4;
}

.product-overview-list {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.product-overview-card {
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  padding: 12px 10px 10px;
  border: 1px solid #edf1f6;
  border-radius: 14px;
  background: #fbfcfe;
}

.product-overview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.product-overview-head > div {
  min-width: 0;
}

.segment-bar {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 34px;
  margin-top: 10px;
  overflow: hidden;
  border-radius: 10px;
  background: #eef2f7;
}

.segment-bar-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 0;
  height: 100%;
  padding: 0 6px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
}

.segment-bar-item span {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.segment-legend-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 8px;
}

.segment-legend-item {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
  color: #64748b;
  font-size: 11px;
  line-height: 14px;
  white-space: normal;
}

.segment-legend-item i {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  margin-top: 3px;
  border-radius: 2px;
}

.member-filter-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.member-filter-row.single-filter {
  grid-template-columns: minmax(0, 1fr);
}

.member-filter-row .van-dropdown-menu {
  height: 34px;
  border-radius: 10px;
  background: #f6f7f9;
  overflow: hidden;
}

.member-filter-row ::v-deep .van-dropdown-menu__bar {
  height: 34px;
  background: transparent;
  box-shadow: none;
}

.member-filter-row ::v-deep .van-dropdown-menu__title {
  max-width: 130px;
  color: #4b5563;
  font-size: 12px;
}

.district-option-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
  background: #fff;
}

.district-option-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(32px, auto) 18px;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 0 16px;
  border: 0;
  color: #323233;
  background: #fff;
  font-size: 14px;
  text-align: left;
}

.district-option-row.active {
  color: #aa341b;
  font-weight: 600;
}

.district-option-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.district-option-count {
  justify-self: end;
  color: #969799;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.district-option-row.active .district-option-count {
  color: #aa341b;
}

.district-option-check {
  justify-self: end;
  font-size: 14px;
}

.member-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.member-card {
  position: relative;
  overflow: hidden;
  padding: 14px 14px 14px 16px;
  border: 1px solid #dfe6ef;
  border-radius: 14px;
  background: #fbfcfe;
  box-shadow: 0 6px 16px rgba(31, 45, 61, 0.06);
}

.member-card::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: #aa341b;
  content: '';
}

.member-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.16s ease;
}

.member-top:active {
  background: #f5f7fa;
}

.member-top > div:first-child {
  flex: 0 0 64px;
  min-width: 64px;
}

.member-side {
  display: grid;
  align-items: flex-start;
  grid-template-columns: minmax(96px, 1fr) minmax(88px, 1fr) 18px;
  flex: 1 1 auto;
  min-width: 0;
  gap: 4px;
}

.member-name {
  overflow: hidden;
  color: #323233;
  font-size: 15px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-meta {
  margin-top: 4px;
  overflow: hidden;
  color: #969799;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  min-width: 0;
  text-align: center;
}

.member-score > span {
  display: block;
  color: #aa341b;
  font-size: 17px;
  line-height: 18px;
  font-weight: 700;
}

.member-score small {
  margin-top: 0;
  color: #969799;
  font-size: 10px;
  line-height: 12px;
}

.member-score small b {
  color: #aa341b;
  font-size: 10px;
  font-weight: 700;
}

.member-coverage-label {
  justify-content: center;
}

.member-coverage-label > span:first-child {
  overflow: visible;
  text-overflow: clip;
}

.member-detail-btn,
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #969799;
  background: transparent;
  font-size: 16px;
  line-height: 1;
}

.member-detail-btn {
  justify-self: end;
  flex: 0 0 18px;
  width: 18px;
  height: 24px;
}

.member-abc-breakdown {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin-top: 10px;
  overflow: hidden;
  border: 1px solid #edf1f6;
  border-radius: 10px;
  background: #edf1f6;
}

.member-abc-breakdown > div {
  display: grid;
  grid-template-columns: auto 1fr;
  min-width: 0;
  padding: 7px 8px;
  background: #fff;
}

.member-abc-breakdown span {
  grid-row: 1 / 3;
  align-self: center;
  margin-right: 6px;
  color: #aa341b;
  font-size: 12px;
  font-weight: 700;
}

.member-abc-breakdown strong,
.member-abc-breakdown small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-abc-breakdown strong {
  color: #323233;
  font-size: 13px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
}

.member-abc-breakdown small {
  color: #969799;
  font-size: 9px;
  line-height: 12px;
}

.member-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 6px;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #edf1f6;
  border-radius: 10px;
  background: #fff;
}

.member-kpis div {
  min-width: 0;
}

.member-kpis strong {
  display: block;
  color: #323233;
  font-size: 15px;
  line-height: 21px;
}

.member-kpis span {
  display: block;
  color: #969799;
  font-size: 11px;
  line-height: 16px;
}

.member-kpis small {
  display: block;
  color: #969799;
  font-size: 9px;
  line-height: 12px;
}

.member-read-duration {
  overflow: visible;
  font-size: 8px;
  line-height: 11px;
  white-space: nowrap;
}

.member-read-duration span {
  display: inline;
  color: #323233;
  font-size: 10px;
  font-weight: 400;
}

.detail-slide-page {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  box-sizing: border-box;
  padding: 0 12px 90px;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(175deg, #e8f1ff 0%, #f4edf5 32%, #f2f5f9 58%);
  -webkit-overflow-scrolling: touch;
}

.detail-slide-enter-active,
.detail-slide-leave-active {
  transition: transform 0.24s ease, opacity 0.24s ease;
}

.detail-slide-enter,
.detail-slide-leave-to {
  transform: translateX(100%);
  opacity: 0.98;
}

.detail-nav {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 -12px 14px;
  padding: 14px 12px 12px;
  background: rgba(242, 245, 249, 0.96);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 0 rgba(226, 232, 240, 0.9);
}

.back-btn {
  flex: 0 0 auto;
}

.member-products {
  margin-top: 10px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}

.detail-panel {
  padding: 15px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.detail-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 600;
}

.detail-metrics-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.detail-metrics-header small {
  color: #969799;
  font-size: 11px;
  line-height: 16px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-item {
  position: relative;
  padding: 12px;
  border: 1px solid #f2f5f9;
  border-radius: 14px;
  background: #fff;
}

.detail-item-clickable {
  padding-right: 30px;
  cursor: pointer;
  border-color: rgba(170, 52, 27, 0.18);
  background: linear-gradient(145deg, #fff 0%, #fff8f5 100%);
}

.detail-item-clickable:active {
  transform: scale(0.985);
}

.detail-item-arrow {
  position: absolute;
  top: 50%;
  right: 11px;
  color: #aa341b;
  transform: translateY(-50%);
}

.detail-promotion-card {
  grid-column: 1 / -1;
  padding-top: 18px;
}

.detail-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: #969799;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-period {
  position: absolute;
  top: 8px;
  right: 12px;
  color: #969799;
  font-size: 10px;
  line-height: 14px;
}

.detail-value {
  display: flex;
  align-items: baseline;
  min-width: 0;
  margin-top: 8px;
  gap: 3px;
  line-height: 24px;
  white-space: nowrap;
}

.detail-value-main {
  min-width: 0;
  overflow: hidden;
  color: #aa341b;
  font-size: 18px;
  font-weight: 700;
  text-overflow: ellipsis;
}

.detail-value-unit {
  flex: 0 0 auto;
  color: #646566;
  font-size: 14px;
  font-weight: 600;
}

.content-detail-slide-page {
  z-index: 2100;
}

.content-detail-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.content-detail-summary > div {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 12px 6px;
  border: 1px solid rgba(170, 52, 27, 0.12);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.94);
}

.content-detail-summary strong {
  color: #aa341b;
  font-size: 20px;
  line-height: 24px;
}

.content-detail-summary span {
  color: #646566;
  font-size: 11px;
}

.content-detail-filters {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.content-detail-filters button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  padding: 9px 5px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.92);
  font-size: 12px;
}

.content-detail-filters button.active {
  border-color: rgba(170, 52, 27, 0.32);
  color: #aa341b;
  background: #fff2ed;
}

.content-detail-filters b {
  font-size: 12px;
}

.content-detail-loading {
  min-height: 220px;
}

.content-detail-error {
  margin-top: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #fff;
}

.content-customer-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.content-customer-card {
  padding: 13px;
  border: 1px solid rgba(226, 232, 240, 0.92);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.content-customer-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.content-customer-avatar {
  display: flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #aa341b;
  background: #fff0eb;
  font-size: 15px;
  font-weight: 700;
}

.content-customer-info {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.content-customer-info strong,
.content-customer-info span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.content-customer-info strong {
  color: #323233;
  font-size: 14px;
}

.content-customer-info span {
  color: #969799;
  font-size: 11px;
}

.content-open-status {
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 999px;
  color: #8b95a5;
  background: #f1f5f9;
  font-size: 10px;
}

.content-open-status.opened {
  color: #0b8f5a;
  background: rgba(16, 185, 129, 0.12);
}

.content-customer-stats {
  display: flex;
  gap: 16px;
  margin-top: 10px;
  padding-left: 46px;
  color: #64748b;
  font-size: 11px;
}

.promotion-loading {
  display: flex;
  align-items: center;
  min-height: 62px;
}

.promotion-main {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}

.promotion-main div {
  min-width: 0;
}

.promotion-main strong {
  color: #aa341b;
  font-size: 22px;
  font-weight: 700;
  line-height: 26px;
}

.promotion-main span {
  color: #646566;
  font-size: 13px;
  font-weight: 600;
}

.promotion-main b {
  flex: 0 0 auto;
  color: #aa341b;
  font-size: 18px;
  line-height: 22px;
}

.promotion-track {
  height: 6px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf2f7;
}

.promotion-fill {
  height: 100%;
  border-radius: 999px;
  background: #aa341b;
}

.promotion-sub {
  margin-top: 8px;
  color: #969799;
  font-size: 12px;
  line-height: 16px;
}

.promotion-sub b {
  color: #323233;
  font-size: 14px;
}

.promotion-note {
  margin-top: 6px;
  color: #969799;
  font-size: 10px;
  line-height: 1.45;
}

.product-title {
  margin: 24px 0 8px;
  color: #333;
  font-size: 15px;
  font-weight: 700;
}

.detail-abc-coverage-grid {
  margin-bottom: 4px;
}

.product-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #f2f5f9;
}

.product-name {
  color: #323233;
  font-size: 14px;
  font-weight: 600;
}

.product-meta {
  margin-top: 4px;
  color: #969799;
  font-size: 12px;
}

.product-row strong {
  color: #aa341b;
  font-size: 16px;
}

.recent-title {
  margin: 24px 0 8px;
  color: #333;
  font-size: 15px;
  font-weight: 700;
}

.detail-loading,
.detail-error {
  padding: 16px 0;
  border-top: 1px solid #f2f5f9;
  color: #969799;
  font-size: 12px;
  text-align: center;
}

.recent-item {
  padding: 12px 0;
  border-top: 1px solid #f2f5f9;
}

.recent-main {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
}

.recent-main small,
.recent-sub {
  color: #969799;
  font-size: 12px;
}

.recent-sub {
  margin-top: 4px;
}

</style>
